import React from 'react';

// ─── Utilities ────────────────────────────────────────────────────────────────

export const slugify = (text) =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '-');

export const extractToc = (content) => {
  const lines = content.trim().split('\n');
  const toc = [];
  lines.forEach((line) => {
    if (line.startsWith('## ')) {
      const raw = line.replace('## ', '').replace(/<[^>]*>/g, '');
      toc.push({ title: raw, id: slugify(raw), level: 2 });
    } else if (line.startsWith('### ')) {
      const raw = line.replace('### ', '').replace(/<[^>]*>/g, '');
      toc.push({ title: raw, id: slugify(raw), level: 3 });
    }
  });
  return toc;
};

export const parseContent = (content) => {
  const lines = content.trim().split('\n');
  const elements = [];
  let inTable = false;
  let tableRows = [];
  let listBuffer = [];

  const flushTable = (key) => {
    if (tableRows.length > 0) {
      elements.push(
        <table key={`table-${key}`} className="legal-table">
          <tbody>{tableRows}</tbody>
        </table>
      );
      tableRows = [];
      inTable = false;
    }
  };

  const flushList = (key) => {
    if (listBuffer.length > 0) {
      elements.push(<ul key={`ul-${key}`} className="legal-ul">{listBuffer}</ul>);
      listBuffer = [];
    }
  };

  const fmt = (text) =>
    text
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/„([^"]+)"/g, '„$1"');

  lines.forEach((line, i) => {
    if (line.startsWith('## ')) {
      flushTable(i); flushList(i);
      const raw = line.replace('## ', '');
      elements.push(
        <h2 key={i} id={slugify(raw.replace(/<[^>]*>/g, ''))} className="legal-h2"
          dangerouslySetInnerHTML={{ __html: fmt(raw) }} />
      );
    } else if (line.startsWith('### ')) {
      flushTable(i); flushList(i);
      const raw = line.replace('### ', '');
      elements.push(
        <h3 key={i} id={slugify(raw.replace(/<[^>]*>/g, ''))} className="legal-h3"
          dangerouslySetInnerHTML={{ __html: fmt(raw) }} />
      );
    } else if (line.startsWith('**') && line.endsWith('**')) {
      flushList(i);
      elements.push(
        <p key={i} className="legal-bold"
          dangerouslySetInnerHTML={{ __html: line.replace(/\*\*/g, '') }} />
      );
    } else if (line.startsWith('- ')) {
      if (inTable) flushTable(i);
      listBuffer.push(
        <li key={i} className="legal-li"
          dangerouslySetInnerHTML={{ __html: fmt(line.replace('- ', '')) }} />
      );
    } else if (line.startsWith('|')) {
      flushList(i);
      inTable = true;
      const cells = line.split('|').filter((c) => c.trim());
      if (line.includes('---')) return;
      const isHeader = tableRows.length === 0;
      tableRows.push(
        <tr key={i}>
          {cells.map((cell, ci) =>
            isHeader
              ? <th key={ci} dangerouslySetInnerHTML={{ __html: cell.trim() }} />
              : <td key={ci} dangerouslySetInnerHTML={{ __html: fmt(cell.trim()) }} />
          )}
        </tr>
      );
    } else if (line.trim()) {
      flushTable(i); flushList(i);
      elements.push(
        <p key={i} className="legal-p"
          dangerouslySetInnerHTML={{ __html: fmt(line) }} />
      );
    }
  });

  flushTable('end'); flushList('end');
  return elements;
};

// ─── Content ──────────────────────────────────────────────────────────────────

const privacy = {
  ro: {
    title: 'Politica de Confidențialitate',
    lastUpdated: '7 Iulie 2026',
    content: `
## 1. Introducere

Poly Tool Design S.R.L. („**Poly Tool**", „**noi**" sau „**Compania**"), înregistrată în România cu CUI RO40293031 și Nr. Reg. Com. J13/3628/2018, cu sediul în Constanța, prelucrează datele dumneavoastră cu caracter personal cu responsabilitate și transparență, în deplină conformitate cu **Regulamentul (UE) 2016/679 (GDPR)** și cu Legea nr. 190/2018 privind măsurile de punere în aplicare a Regulamentului.

Prin „date cu caracter personal" înțelegem orice informație care permite identificarea directă sau indirectă a unei persoane fizice. Această politică descrie ce date colectăm, de ce, cum le utilizăm, cu cine le partajăm, cât timp le păstrăm și care sunt drepturile dumneavoastră legale.

Politica se aplică tuturor produselor și serviciilor Poly Tool Design: site-ul **polytooldesign.com**, platforma <a href="https://ortexo.com" target="_blank" rel="noopener noreferrer">**Ortexo**</a>, aplicația **Borderou** și aplicația **Plicuri**.

## 2. Produse și Servicii Acoperite

### 2.1 Site-ul polytooldesign.com

Site-ul de prezentare al companiei, disponibil la <a href="https://polytooldesign.com">polytooldesign.com</a>. Oferă informații despre produsele Poly Tool, un formular de contact și posibilitatea de abonare la newsletter. Nu este necesară crearea unui cont pentru a-l utiliza.

### 2.2 Ortexo — Platformă Juridică AI

<a href="https://ortexo.com" target="_blank" rel="noopener noreferrer">Ortexo</a> este o platformă premium de cercetare juridică asistată de inteligență artificială, disponibilă la ortexo.com. Oferă acces la peste 500.000 de acte normative românești, căutare jurisprudențială (ÎCCJ), analiză și comparare de documente legislative, un consultant juridic AI disponibil 24/7, calendar și workspace integrat. <a href="/#features">Detalii pe polytooldesign.com →</a>

### 2.3 Borderou & Corespondenţă — Aplicație Desktop

<a href="/borderou">Borderou</a> și <a href="/corespondenta">Corespondenţă</a> sunt modulele principale ale unei aplicații desktop Electron pentru gestionarea dosarelor, termenelor, corespondenței profesionale și calendarului de activitate, cu sincronizare în cloud multi-utilizator.

### 2.4 Plicuri — Corespondență & Documente pentru Insolvență

**Plicuri** este o aplicație (desktop Electron + backend cloud) destinată practicienilor în insolvență: gestionarea destinatarilor, generarea plicurilor, a borderourilor poștale și a confirmărilor de primire, analiza automată a sentințelor/încheierilor de insolvență, gestionarea dosarelor și a corespondenței, și redactarea asistată de documente („Documente AI"). Spre deosebire de versiunile anterioare, care funcționau exclusiv local, Plicuri utilizează acum un **backend cloud** (Cloudflare Worker + bază de date D1 + stocare R2), **autentificare Firebase** și **procesare AI în Google Vertex AI** (regiunea europe-west1, UE) pentru extragerea datelor din documente.

## 3. Ce Date Colectăm

### 3.1 Site-ul polytooldesign.com

**Date furnizate direct de dumneavoastră:**
- Formular de contact: nume și prenume, adresă de email, conținutul mesajului
- Abonare newsletter: adresă de email

**Date colectate automat:**
- Date tehnice: adresă IP, tip browser, versiune sistem de operare, rezoluție ecran
- Date de navigare: pagini vizitate, durată sesiune, pagina de proveniență (referrer)
- Date prin cookie-uri și localStorage (detalii complete în <a href="/cookies">Politica de Cookie-uri</a>)

### 3.2 Platforma Ortexo (ortexo.com)

**Date de cont și identitate:**
- Nume și prenume, adresă de email, parolă (stocată ca hash securizat — nu avem acces la parola în clar)
- Date de facturare: nume/denumire firmă, adresă, cod fiscal (CUI sau CNP) — necesare emiterii facturilor legale

**Date de utilizare a platformei:**
- Interogările introduse în motorul de căutare juridică și în asistentul AI
- Documente legislative accesate, descărcate sau marcate ca favorite
- Conversații cu asistentul juridic AI (stocate pentru continuitate sesiune)
- Preferințe de interfață și setări personalizate de cont

**Date de abonament și plată:**
- Planul ales (Gratuit, Civis, Lexis, Praetor), data activării, istoricul modificărilor
- Istoricul plăților: dată, sumă, stare tranzacție — datele cardului bancar sunt procesate **exclusiv de Stripe** și nu sunt stocate pe serverele noastre

**Date tehnice și de securitate:**
- Adresă IP la autentificare, marcaje de timp (timestamps), jurnale de erori
- Token-uri de sesiune, informații despre dispozitivele utilizate (pentru detectarea accesului neautorizat)

### 3.3 Aplicația Borderou & Corespondenţă

**Date de autentificare:**
- Adresă de email, parolă (gestionată prin Firebase Authentication — stocată ca hash, fără acces la parola în clar)
- Token de sesiune stocat local pe dispozitiv

**Date organizaționale:**
- Denumirea organizației, logo (opțional)
- Datele membrilor organizației: email, rol (proprietar / membru), data aderării
- Invitații active: email destinatar, dată trimitere, statut (acceptată / în așteptare)

**Date de lucru introduse de utilizator:**
- Dosare: titlu, număr dosar, instanță, stadiu, note, termene, sarcini, documente atașate
- Corespondență: expeditor, destinatar(i), obiect, conținut, atașamente
- Evenimente calendar: titlu, dată, oră, dosar asociat, participanți
- To-do items: titlu, prioritate, termen limită, statut

**Date tehnice:**
- Preferințe interfață (dimensiune text, modul activ, stare panouri)
- Jurnale sincronizare cloud (timestamp, tip operație, statut)
- Versiunea aplicației și sistemul de operare (utilizate pentru suport tehnic și actualizări automate)

### 3.4 Plicuri

**Date de autentificare:** adresă de email și parolă (gestionate prin Firebase Authentication — parola stocată ca hash, fără acces la parola în clar), token de sesiune.

**Date de lucru stocate în cloud** (Cloudflare D1 + R2, cu izolare per organizație):
- Dosare de insolvență: număr dosar, instanță, debitor (denumire, CUI, sediu), creditori, administrator/lichidator judiciar, termene, stadiu
- Sentințe/încheieri încărcate (PDF) și datele extrase din acestea
- Destinatari și firme: denumire, CUI, adresă, reprezentant legal, date de contact
- Corespondență și documente generate (notificări, plicuri, borderouri, confirmări, documente „Documente AI")
- Profilul practicianului (cabinet), listele de destinatari, istoricul generărilor, creditele și tranzacțiile

**Date trimise către procesare AI:** conținutul documentelor de insolvență (text, PDF-uri, fișiere de probe) este transmis către **Google Vertex AI (europe-west1, UE)** pentru extragerea structurată a datelor. Prelucrarea are loc în UE; pe planul plătit, datele **nu sunt utilizate pentru antrenarea modelelor** (detalii în secțiunea 10).

**Date către servicii externe:** validare CUI prin **API-ul public ANAF**; căutări de informații publice despre firme/tribunale prin **Brave Search / DuckDuckGo**; interogări de dosare pe **portal.just.ro**.

**Notă privind rolurile GDPR:** documentele de insolvență conțin date cu caracter personal (debitori, creditori, reprezentanți, magistrați). Practicianul-utilizator acționează în calitate de **operator**, iar Poly Tool Design în calitate de **persoană împuternicită (procesator)**, prelucrând aceste date exclusiv pentru furnizarea serviciului.

## 4. Scopul Prelucrării Datelor

Prelucrăm datele dumneavoastră exclusiv pentru scopuri determinate, explicite și legitime:

**Furnizarea și operarea serviciilor:**
- Răspuns la mesajele primite prin formularul de contact (polytooldesign.com)
- Trimiterea newsletter-ului, exclusiv cu consimțământul dumneavoastră explicit
- Crearea, gestionarea și securizarea contului pe Ortexo sau Borderou
- Furnizarea funcționalităților Ortexo: căutare, analiză documente, asistent AI
- Sincronizarea datelor Borderou între dispozitive și membrii organizației

**Administrare și facturare:**
- Procesarea plăților și emiterea facturilor fiscale legale
- Gestionarea abonamentelor: activare, reînnoire, modificare, anulare
- Comunicări de cont: confirmare înregistrare, resetare parolă, notificări expirare abonament

**Îmbunătățire și securitate:**
- Analiza traficului și comportamentului utilizatorilor în scopul optimizării produselor
- Detectarea și prevenirea accesului neautorizat, a fraudelor și abuzurilor
- Diagnosticarea și remedierea erorilor tehnice
- Testarea și dezvoltarea de funcționalități noi

**Obligații legale:**
- Arhivarea documentelor fiscale conform Legii nr. 82/1991 (Legea contabilității)
- Răspunsul la solicitările legale ale autorităților competente

## 5. Temeiul Legal al Prelucrării

Fiecare activitate de prelucrare se întemeiază pe cel puțin un temei legal conform art. 6 din GDPR:

| Activitate de prelucrare | Temei legal |
|--------------------------|-------------|
| Newsletter | Art. 6(1)(a) — Consimțământ explicit |
| Cookie-uri analitice și de marketing | Art. 6(1)(a) — Consimțământ explicit |
| Răspuns la formularul de contact | Art. 6(1)(f) — Interes legitim |
| Gestionarea contului și furnizarea serviciului | Art. 6(1)(b) — Executarea contractului |
| Procesarea plăților | Art. 6(1)(b) + (c) — Contract + Obligație legală |
| Arhivare documente fiscale | Art. 6(1)(c) — Obligație legală (Legea nr. 82/1991) |
| Securitate și prevenire fraude | Art. 6(1)(f) — Interes legitim |
| Analiză și îmbunătățire produse (date agregate) | Art. 6(1)(f) — Interes legitim |
| Răspuns la solicitări autorități | Art. 6(1)(c) — Obligație legală |

Consimțământul poate fi retras oricând, fără a afecta legalitatea prelucrărilor anterioare retragerii.

## 6. Furnizori de Servicii Terți

Colaborăm cu furnizori de servicii selectați cu atenție, cu care am încheiat **Acorduri de Prelucrare a Datelor (DPA)** conform art. 28 GDPR. Nu vindem niciodată datele dumneavoastră.

| Furnizor | Serviciu | Produs | Jurisdicție | Garanție transfer |
|----------|----------|--------|-------------|-------------------|
| Web3Forms | Formulare contact | polytooldesign.com | UE/SUA | Clauze Contractuale Standard (SCC) |
| Mailchimp (Intuit Inc.) | Newsletter | polytooldesign.com | SUA | SCC |
| Google Analytics | Analiză trafic | polytooldesign.com | SUA | SCC |
| Formspree | Formulare contact | ortexo.com | SUA | SCC |
| Google Cloud (europe-west1) | Infrastructură backend | Ortexo | UE | — |
| Google Cloud — Vertex AI (europe-west1) | Extragere AI din documente (sentințe, PDF-uri, probe) | Borderou, Plicuri | UE | — (prelucrare în UE) |
| Stripe Inc. | Procesare plăți | Ortexo, Plicuri | SUA | SCC + Data Privacy Framework |
| Firebase / Google LLC | Autentificare + DB cloud | Borderou & Corespondenţă, Plicuri | SUA/UE | SCC |
| Cloudflare Inc. | API backend + bază de date (D1) + stocare (R2) | Borderou & Corespondenţă, Plicuri | SUA/UE | SCC |
| Brave Software / DuckDuckGo | Căutare informații publice | Plicuri | SUA/UE | SCC |
| ANAF | Validare CUI (API public) | Borderou & Corespondenţă, Plicuri | România (UE) | — |

## 7. Transferuri Internaționale de Date

Unii furnizori prelucrează date în **Statele Unite ale Americii** sau în alte țări din afara Spațiului Economic European (SEE). Transferurile sunt efectuate cu garanții adecvate conform Capitolului V din GDPR:

- **Clauze Contractuale Standard (SCC)** adoptate prin Decizia Comisiei Europene 2021/914/UE — utilizate cu Mailchimp, Stripe, Firebase, Cloudflare, Formspree, Web3Forms
- **EU-U.S. Data Privacy Framework (DPF)** — acolo unde furnizorul deține certificare activă
- **Infrastructură europeană** — serverele Ortexo sunt localizate fizic în **Google Cloud europe-west1 (Belgia)**, fără transfer în afara UE
- **Procesare AI în UE (Plicuri și Borderou)** — analiza documentelor prin **Google Vertex AI** se efectuează în regiunea **europe-west1 (Belgia)**, fără transfer în afara UE

Puteți solicita o copie a garanțiilor aplicabile contactând-ne la contact@polytooldesign.com.

## 8. Perioadele de Stocare a Datelor

Datele personale sunt păstrate numai atât timp cât este necesar scopului colectării sau cât impune legea:

| Categorie de date | Perioadă de stocare |
|-------------------|---------------------|
| Mesaje formular de contact | 3 ani de la data trimiterii |
| Date newsletter (email) | Până la dezabonare + 30 de zile |
| Date cont Ortexo — cont activ | Pe durata existenței contului |
| Date cont Ortexo — după dezactivare | 12 luni, apoi ștergere definitivă |
| Dosare și date Borderou — activ | Pe durata utilizării |
| Dosare și date Borderou — după ștergere | 30 de zile recuperare, apoi ștergere permanentă |
| Facturi și documente fiscale | **10 ani** (Legea nr. 82/1991, art. 25) |
| Jurnale tehnice și de securitate | 90 de zile |
| Date cookie-uri analitice | Maxim 2 ani |
| Preferințe cookie-uri (consimțământ) | 12 luni |

La expirarea perioadei aplicabile, datele sunt **șterse definitiv** sau **anonimizate ireversibil**.

## 9. Drepturile Dumneavoastră conform GDPR

Conform art. 15–22 din GDPR, beneficiați de următoarele drepturi:

**Dreptul de acces (art. 15)** — Puteți solicita o copie a datelor personale pe care le deținem despre dumneavoastră, inclusiv informații despre originea, scopul și destinatarii lor.

**Dreptul la rectificare (art. 16)** — Puteți solicita corectarea datelor inexacte sau completarea celor incomplete. Utilizatorii Ortexo și Borderou pot actualiza direct datele de bază din setările contului.

**Dreptul la ștergere / „dreptul de a fi uitat" (art. 17)** — Puteți solicita ștergerea datelor dumneavoastră atunci când nu mai există un temei legal valabil. Excepție: datele pe care suntem obligați legal să le păstrăm (ex.: facturi fiscale).

**Dreptul la restricționarea prelucrării (art. 18)** — Puteți solicita suspendarea temporară a prelucrării datelor dumneavoastră în anumite circumstanțe (ex.: contestarea exactității datelor, în timp ce analizăm solicitarea).

**Dreptul la portabilitate (art. 20)** — Puteți primi datele furnizate de dumneavoastră în format structurat, utilizat frecvent și citibil automat (JSON/CSV), și le puteți transmite altui operator, acolo unde prelucrarea se bazează pe consimțământ sau contract.

**Dreptul de opoziție (art. 21)** — Vă puteți opune prelucrării bazate pe interes legitim (inclusiv profilare) sau în scopuri de marketing direct. La primirea opoziției față de marketing, vom înceta imediat prelucrarea.

**Dreptul de a nu face obiectul deciziilor automate (art. 22)** — Nu luăm decizii cu efecte juridice semnificative bazate exclusiv pe prelucrare automată sau profilare.

**Dreptul de retragere a consimțământului** — Consimțământul poate fi retras oricând (dezabonare newsletter, modificarea preferințelor cookie-uri), fără a afecta prelucrările anterioare.

**Cum să vă exercitați drepturile:** Trimiteți o solicitare scrisă la **contact@polytooldesign.com** cu subiectul „Drept GDPR — [tipul dreptului solicitat]". Vom răspunde în termen de **30 de zile calendaristice**. În cazuri complexe, termenul poate fi prelungit cu maximum 60 de zile, cu notificarea dumneavoastră prealabilă.

## 10. Securitatea Datelor

Implementăm măsuri tehnice și organizatorice adecvate nivelului de risc, în conformitate cu art. 32 din GDPR:

**Măsuri tehnice:**
- Criptare în tranzit: HTTPS/TLS 1.3 pentru toate comunicările
- Criptare în repaus: AES-256 pentru datele stocate în cloud (Ortexo, Borderou)
- Parole: hash bcrypt cu salt unic per utilizator — nu stocăm niciodată parole în clar
- Autentificare: Firebase Authentication cu JWT cu durată limitată + token refresh securizat
- Acces bazat pe roluri (RBAC): separare strictă între organizații în Borderou
- Infrastructură Ortexo pe Google Cloud europe-west1 cu izolare la nivel de proiect
- Actualizări automate de securitate pentru dependențele aplicațiilor

**Măsuri organizatorice:**
- Acces restricționat la datele utilizatorilor — numai personalul autorizat cu nevoie justificată (need-to-know)
- Angajamente de confidențialitate pentru tot personalul cu acces la date
- Proceduri documentate de răspuns la incidente de securitate
- Revizuiri periodice ale controalelor de acces și securitate

**AI și datele dumneavoastră:** Interacțiunile cu asistentul AI Ortexo **nu sunt utilizate pentru antrenarea modelelor de limbaj** fără consimțământul explicit al dumneavoastră. Conversațiile sunt procesate în timp real și stocate exclusiv pentru furnizarea continuă a serviciului. În Plicuri și Borderou, analiza documentelor de insolvență se efectuează prin **Google Vertex AI în regiunea europe-west1 (UE)**; pe planul plătit, Google **nu utilizează** conținutul trimis pentru antrenarea modelelor și nu îl reține dincolo de necesitățile operaționale.

**Notificare incidente:** În cazul unui incident de securitate care vă afectează datele, vom notifica ANSPDCP în termen de **72 de ore** și vă vom informa direct dacă incidentul prezintă un risc ridicat pentru drepturile dumneavoastră.

## 11. Protecția Minorilor

Serviciile noastre sunt destinate exclusiv persoanelor cu vârsta de minimum **16 ani**. Nu colectăm cu intenție date personale de la minori sub această vârstă.

Dacă suspectați că un minor a furnizat date prin serviciile noastre, vă rugăm să ne contactați imediat la contact@polytooldesign.com. Vom șterge aceste date de îndată ce le identificăm.

## 12. Cookie-uri și Tehnologii Similare

Utilizăm cookie-uri și tehnologii similare (localStorage, pixeli de urmărire) pe site-ul polytooldesign.com. Detalii complete — lista tuturor cookie-urilor, scopurile, duratele și opțiunile de gestionare — sunt disponibile în <a href="/cookies">Politica de Cookie-uri →</a>

## 13. Modificări ale Politicii

Putem actualiza această politică periodic pentru a reflecta modificări ale serviciilor, ale cerințelor legale sau ale practicilor de prelucrare.

- **Modificări minore** (clarificări, corecturi de formă): actualizăm data documentului
- **Modificări semnificative**: notificare prin email cu minimum **14 zile** înainte de intrarea în vigoare, adresată utilizatorilor cu cont activ

Versiunile anterioare sunt arhivate și disponibile la cerere. Continuarea utilizării serviciilor după intrarea în vigoare constituie acceptarea noii versiuni.

## 14. Date de Contact

Pentru orice întrebări privind prelucrarea datelor sau pentru exercitarea drepturilor GDPR:

**Poly Tool Design S.R.L.**
Constanța, România
CUI: RO40293031 | Nr. Reg. Com.: J13/3628/2018
Email: <a href="mailto:contact@polytooldesign.com">contact@polytooldesign.com</a>

Răspundem în termen de 30 de zile calendaristice.

## 15. Autoritatea de Supraveghere

Aveți dreptul de a depune o plângere la autoritatea de supraveghere competentă dacă considerați că prelucrarea datelor dumneavoastră încalcă GDPR:

**Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal (ANSPDCP)**
Adresă: B-dul G-ral. Gheorghe Magheru 28-30, Sector 1, 010336 București
Website: <a href="https://www.dataprotection.ro" target="_blank" rel="noopener noreferrer">www.dataprotection.ro</a>
Email: anspdcp@dataprotection.ro | Tel: +40.318.059.211

Vă încurajăm să ne contactați direct în primul rând — vom depune toate eforturile pentru a rezolva orice preocupare în mod amiabil și rapid.
    `
  },
  en: {
    title: 'Privacy Policy',
    lastUpdated: 'July 7, 2026',
    content: `
## 1. Introduction

Poly Tool Design S.R.L. ("**Poly Tool**", "**we**", "**us**" or "**the Company**"), registered in Romania under CUI RO40293031 and Trade Register No. J13/3628/2018, headquartered in Constanța, processes your personal data responsibly and transparently, in full compliance with **Regulation (EU) 2016/679 (GDPR)** and Romanian Law No. 190/2018 implementing the Regulation.

"Personal data" means any information that directly or indirectly identifies a natural person. This policy describes what data we collect, why we collect it, how we use it, who we share it with, how long we keep it, and what your legal rights are.

This policy applies to all Poly Tool Design products and services: the **polytooldesign.com** website, the <a href="https://ortexo.com" target="_blank" rel="noopener noreferrer">**Ortexo**</a> platform, the **Borderou** application, and the **Plicuri** application.

## 2. Products and Services Covered

### 2.1 polytooldesign.com Website

The company's presentation website, available at <a href="https://polytooldesign.com">polytooldesign.com</a>. It provides information about Poly Tool's products, a contact form, and the option to subscribe to our newsletter. No account creation is required.

### 2.2 Ortexo — AI Legal Platform

<a href="https://ortexo.com" target="_blank" rel="noopener noreferrer">Ortexo</a> is a premium AI-powered legal research platform available at ortexo.com. It provides access to over 500,000 Romanian legislative acts, case law search (Supreme Court — ÎCCJ), legislative document analysis and comparison, a 24/7 AI legal assistant, and an integrated calendar and workspace. <a href="/#features">Details on polytooldesign.com →</a>

### 2.3 Borderou & Corespondenţă — Desktop Application

<a href="/borderou">Borderou</a> and <a href="/corespondenta">Corespondenţă</a> are the main modules of an Electron desktop application for managing legal cases, deadlines, professional correspondence and activity calendars, with multi-user cloud synchronization.

### 2.4 Plicuri — Correspondence & Documents for Insolvency

**Plicuri** is an application (Electron desktop + cloud backend) for insolvency practitioners: managing recipients, generating envelopes, postal bordereaux and delivery confirmations, automatically analyzing insolvency court decisions, managing cases and correspondence, and AI-assisted document drafting ("Documente AI"). Unlike earlier versions, which ran exclusively offline, Plicuri now uses a **cloud backend** (Cloudflare Worker + D1 database + R2 storage), **Firebase authentication**, and **AI processing in Google Vertex AI** (europe-west1 region, EU) to extract data from documents.

## 3. What Data We Collect

### 3.1 polytooldesign.com Website

**Data you provide directly:**
- Contact form: first name, last name, email address, message content
- Newsletter subscription: email address

**Automatically collected data:**
- Technical data: IP address, browser type, operating system version, screen resolution
- Navigation data: pages visited, session duration, referring page
- Data via cookies and localStorage (full details in our <a href="/cookies">Cookie Policy</a>)

### 3.2 Ortexo Platform (ortexo.com)

**Account and identity data:**
- First name, last name, email address, password (stored as a secure hash — we cannot access your password in plain text)
- Billing data: name/company name, address, tax identification number (CUI or CNP) — required for issuing legal invoices

**Platform usage data:**
- Queries entered in the legal search engine and AI assistant
- Legislative documents accessed, downloaded, or bookmarked
- AI legal assistant conversations (stored for session continuity)
- Interface preferences and personalized account settings

**Subscription and payment data:**
- Chosen plan (Free, Civis, Lexis, Praetor), activation date, modification history
- Payment history: date, amount, transaction status — card data is processed **exclusively by Stripe** and is never stored on our servers

**Technical and security data:**
- IP address at login, timestamps, error logs
- Session tokens, device information (for detecting unauthorized access)

### 3.3 Borderou & Corespondenţă Application

**Authentication data:**
- Email address, password (managed via Firebase Authentication — stored as hash, never in plain text)
- Session token stored locally on the device

**Organizational data:**
- Organization name, logo (optional)
- Member data: email, role (owner / member), date joined
- Active invitations: recipient email, sent date, status

**Work data entered by the user:**
- Cases: title, case number, court, status, notes, deadlines, tasks, attached documents
- Correspondence: sender, recipient(s), subject, content, attachments
- Calendar events: title, date, time, associated case, participants
- To-do items: title, priority, deadline, status

**Technical data:**
- Interface preferences (text size, active module, panel states)
- Cloud sync logs (timestamp, operation type, status)
- Application version and operating system (for technical support and automatic updates)

### 3.4 Plicuri

**Authentication data:** email address and password (managed via Firebase Authentication — password stored as a hash, never in plain text), session token.

**Work data stored in the cloud** (Cloudflare D1 + R2, with per-organization isolation):
- Insolvency cases: case number, court, debtor (name, tax ID, registered office), creditors, judicial administrator/liquidator, deadlines, status
- Uploaded court decisions (PDF) and the data extracted from them
- Recipients and companies: name, tax ID, address, legal representative, contact details
- Generated correspondence and documents (notices, envelopes, bordereaux, confirmations, "Documente AI" documents)
- Practitioner (office) profile, recipient lists, generation history, credits and transactions

**Data sent for AI processing:** the content of insolvency documents (text, PDFs, evidence files) is sent to **Google Vertex AI (europe-west1, EU)** for structured data extraction. Processing takes place in the EU; on the paid tier, the data is **not used to train models** (see Section 10).

**Data to external services:** tax-ID validation via the **public ANAF API**; public company/court information searches via **Brave Search / DuckDuckGo**; case lookups on **portal.just.ro**.

**GDPR roles note:** insolvency documents contain personal data (debtors, creditors, representatives, magistrates). The practitioner-user acts as the **controller**, and Poly Tool Design as the **processor**, processing this data solely to provide the service.

## 4. Why We Process Your Data

We process your data exclusively for specified, explicit, and legitimate purposes:

**Service delivery and operation:**
- Responding to messages submitted via the contact form
- Sending the newsletter, exclusively with your explicit consent
- Creating, managing, and securing your account on Ortexo or Borderou
- Delivering Ortexo's features: search, document analysis, AI assistant
- Synchronizing Borderou data across devices and organization members

**Administration and billing:**
- Processing payments and issuing legally compliant invoices
- Managing subscriptions: activation, renewal, modification, cancellation
- Account communications: registration confirmation, password reset, subscription expiry notifications

**Improvement and security:**
- Analyzing traffic and user behavior to optimize our products
- Detecting and preventing unauthorized access, fraud, and abuse
- Diagnosing and fixing technical errors
- Testing and developing new features

**Legal obligations:**
- Archiving fiscal documents under Romanian Accounting Law No. 82/1991
- Responding to lawful requests from competent authorities

## 5. Legal Basis for Processing

Each processing activity is based on at least one legal ground under GDPR Article 6:

| Processing activity | Legal basis |
|---------------------|-------------|
| Newsletter | Art. 6(1)(a) — Explicit consent |
| Analytics and marketing cookies | Art. 6(1)(a) — Explicit consent |
| Contact form response | Art. 6(1)(f) — Legitimate interest |
| Account management and service delivery | Art. 6(1)(b) — Contract performance |
| Payment processing | Art. 6(1)(b) + (c) — Contract + Legal obligation |
| Fiscal document archiving | Art. 6(1)(c) — Legal obligation (Law No. 82/1991) |
| Security and fraud prevention | Art. 6(1)(f) — Legitimate interest |
| Product analytics (aggregated) | Art. 6(1)(f) — Legitimate interest |
| Responding to authority requests | Art. 6(1)(c) — Legal obligation |

Consent may be withdrawn at any time without affecting the lawfulness of prior processing.

## 6. Third-Party Service Providers

We engage carefully selected service providers with whom we have executed **Data Processing Agreements (DPAs)** under GDPR Article 28. We never sell your data.

| Provider | Service | Product | Jurisdiction | Transfer safeguard |
|----------|---------|---------|--------------|-------------------|
| Web3Forms | Contact forms | polytooldesign.com | EU/USA | Standard Contractual Clauses (SCC) |
| Mailchimp (Intuit Inc.) | Newsletter | polytooldesign.com | USA | SCC |
| Google Analytics | Traffic analytics | polytooldesign.com | USA | SCC |
| Formspree | Contact forms | ortexo.com | USA | SCC |
| Google Cloud (europe-west1) | Backend infrastructure | Ortexo | EU | — |
| Google Cloud — Vertex AI (europe-west1) | AI extraction from documents (court decisions, PDFs, evidence) | Borderou, Plicuri | EU | — (processed in the EU) |
| Stripe Inc. | Payment processing | Ortexo, Plicuri | USA | SCC + Data Privacy Framework |
| Firebase / Google LLC | Authentication + Cloud DB | Borderou & Corespondenţă, Plicuri | USA/EU | SCC |
| Cloudflare Inc. | Backend API + database (D1) + storage (R2) | Borderou & Corespondenţă, Plicuri | USA/EU | SCC |
| Brave Software / DuckDuckGo | Public information search | Plicuri | USA/EU | SCC |
| ANAF | Public CUI validation API | Borderou & Corespondenţă, Plicuri | Romania (EU) | — |

## 7. International Data Transfers

Some providers listed above process data in the **United States** or other countries outside the European Economic Area (EEA). Transfers are carried out with appropriate safeguards per GDPR Chapter V:

- **Standard Contractual Clauses (SCC)** under Commission Decision 2021/914/EU — used with Mailchimp, Stripe, Firebase, Cloudflare, Formspree, Web3Forms
- **EU-U.S. Data Privacy Framework (DPF)** — where the provider holds active certification
- **European infrastructure** — Ortexo servers are physically located in **Google Cloud europe-west1 (Belgium)**, with no transfer outside the EU
- **EU AI processing (Plicuri and Borderou)** — document analysis via **Google Vertex AI** is performed in the **europe-west1 (Belgium)** region, with no transfer outside the EU

You may request a copy of the applicable safeguards by contacting us at contact@polytooldesign.com.

## 8. How Long We Keep Your Data

Personal data is retained only as long as necessary for the collection purpose or as required by law:

| Data category | Retention period |
|---------------|-----------------|
| Contact form messages | 3 years from submission date |
| Newsletter subscription (email) | Until unsubscription + 30 days |
| Ortexo account data — active | Duration of account existence |
| Ortexo account data — after deactivation | 12 months, then permanent deletion |
| Borderou cases and data — active | Duration of use |
| Borderou data — after deletion | 30-day recovery window, then permanent deletion |
| Invoices and fiscal documents | **10 years** (Law No. 82/1991, Art. 25) |
| Technical and security logs | 90 days |
| Analytics cookie data | Max 2 years (Google Analytics) |
| Cookie consent preferences | 12 months |

Upon expiry, data is **permanently deleted** or **irreversibly anonymized**.

## 9. Your Rights Under GDPR

Under GDPR Articles 15–22, you have the following rights:

**Right of access (Art. 15)** — You may request a copy of the personal data we hold about you, along with information about its origin, purpose, and recipients.

**Right to rectification (Art. 16)** — You may request correction of inaccurate data or completion of incomplete data. Ortexo and Borderou users can update basic data directly in account settings.

**Right to erasure / "right to be forgotten" (Art. 17)** — You may request deletion of your data where there is no longer a valid legal basis for processing. Exception: data we are legally required to retain (e.g., invoices).

**Right to restriction of processing (Art. 18)** — You may request temporary suspension of processing in certain circumstances (e.g., while we review a dispute about data accuracy).

**Right to data portability (Art. 20)** — You may receive data you provided in a structured, commonly used, machine-readable format (JSON/CSV) and transmit it to another controller, where processing is based on consent or contract.

**Right to object (Art. 21)** — You may object to processing based on legitimate interests (including profiling) or to direct marketing. We will immediately stop direct marketing upon receiving your objection.

**Right not to be subject to automated decisions (Art. 22)** — We do not make decisions with significant legal effects based solely on automated processing or profiling.

**Right to withdraw consent** — Consent may be withdrawn at any time (newsletter unsubscription, cookie preference changes) without affecting prior processing.

**How to exercise your rights:** Send a written request to **contact@polytooldesign.com** with the subject line "GDPR Right — [type of right requested]". We will respond within **30 calendar days**. In complex cases, the deadline may be extended by up to 60 days, with prior notice to you.

## 10. Data Security

We implement technical and organizational measures appropriate to the level of risk, in accordance with GDPR Article 32:

**Technical measures:**
- Encryption in transit: HTTPS/TLS 1.3 for all communications
- Encryption at rest: AES-256 for data stored in the cloud (Ortexo, Borderou)
- Passwords: bcrypt hashing with unique per-user salt — we never store passwords in plain text
- Authentication: Firebase Authentication with limited-duration JWT + secure token refresh
- Role-based access control (RBAC): strict separation between organizations in Borderou
- Ortexo infrastructure on Google Cloud europe-west1 with project-level isolation
- Automatic security updates for application dependencies

**Organizational measures:**
- Restricted access to user data — only authorized personnel with a justified need-to-know basis
- Confidentiality commitments for all staff with access to data
- Documented incident response procedures
- Periodic reviews of access controls and security settings

**AI and your data:** Interactions with the Ortexo AI assistant **are not used to train language models** without your explicit consent. Conversations are processed in real time and stored solely to deliver the service. In Plicuri and Borderou, insolvency-document analysis is performed via **Google Vertex AI in the europe-west1 (EU) region**; on the paid tier, Google **does not use** the submitted content to train models and does not retain it beyond operational needs.

**Incident notification:** In the event of a security incident affecting your data, we will notify the supervisory authority (ANSPDCP) within **72 hours** and inform you directly if the incident poses a high risk to your rights.

## 11. Children's Privacy

Our services are intended exclusively for individuals aged **16 or over**. We do not knowingly collect personal data from children under this age.

If you suspect that a minor has provided data through our services, please contact us immediately at contact@polytooldesign.com. We will delete such data as soon as we identify it.

## 12. Cookies and Similar Technologies

We use cookies and similar technologies (localStorage, tracking pixels) on the polytooldesign.com website. Full details — including a complete list of cookies, their purposes, durations, and management options — are available in our <a href="/cookies">Cookie Policy →</a>

## 13. Changes to This Policy

We may update this policy periodically to reflect changes in our services, legal requirements, or data processing practices.

- **Minor changes** (clarifications, typographical corrections): we update the document date
- **Significant changes**: email notification at least **14 days** before the change takes effect, addressed to users with an active account

Previous versions are archived and available upon request. Continued use of our services after the effective date constitutes acceptance of the updated policy.

## 14. Contact Us

For any questions about data processing or to exercise your GDPR rights:

**Poly Tool Design S.R.L.**
Constanța, Romania
CUI: RO40293031 | Trade Register: J13/3628/2018
Email: <a href="mailto:contact@polytooldesign.com">contact@polytooldesign.com</a>

We respond within 30 calendar days.

## 15. Supervisory Authority

You have the right to lodge a complaint with the competent supervisory authority if you believe that the processing of your data violates the GDPR:

**National Supervisory Authority for Personal Data Processing (ANSPDCP)**
Address: B-dul G-ral. Gheorghe Magheru 28-30, Sector 1, 010336 Bucharest, Romania
Website: <a href="https://www.dataprotection.ro" target="_blank" rel="noopener noreferrer">www.dataprotection.ro</a>
Email: anspdcp@dataprotection.ro | Phone: +40.318.059.211

We encourage you to contact us directly first — we will make every effort to resolve any concern amicably and promptly.
    `
  }
};

const terms = {
  ro: {
    title: 'Termeni și Condiții',
    lastUpdated: '7 Iulie 2026',
    content: `
## 1. Informații despre Companie

Acești Termeni și Condiții („**Termenii**") sunt un acord juridic obligatoriu între dumneavoastră și Poly Tool Design S.R.L., guvernând utilizarea tuturor produselor și serviciilor noastre.

**Poly Tool Design S.R.L.**
Sediu: Constanța, România | CUI: RO40293031 | Nr. Reg. Com.: J13/3628/2018
Email: <a href="mailto:contact@polytooldesign.com">contact@polytooldesign.com</a>
Website: <a href="https://polytooldesign.com">polytooldesign.com</a>

## 2. Definiții

- **„Servicii"** — toate produsele, platformele și aplicațiile Poly Tool Design
- **„Utilizator" / „dumneavoastră"** — orice persoană fizică sau juridică care accesează sau utilizează Serviciile
- **„Cont"** — profilul creat de utilizator pentru accesul la serviciile care necesită autentificare
- **„Conținut"** — orice text, documente, date sau alte materiale încărcate sau create de utilizator
- **„Abonament"** — accesul plătit la funcționalitățile premium ale Ortexo sau Borderou

## 3. Acceptarea Termenilor

Prin accesarea site-ului polytooldesign.com, crearea unui cont pe oricare dintre platformele noastre, sau instalarea și utilizarea aplicațiilor Poly Tool Design, confirmați că ați citit, înțeles și acceptat integral acești Termeni și <a href="/privacy-policy">Politica de Confidențialitate</a>.

Dacă utilizați Serviciile în numele unei persoane juridice (firmă, instituție), garantați că aveți autoritatea de a angaja acea entitate și că aceasta acceptă acești Termeni.

Dacă nu sunteți de acord cu oricare dintre acești Termeni, nu utilizați Serviciile noastre.

## 4. Descrierea Serviciilor

### 4.1 Site-ul polytooldesign.com

Situl web de prezentare al companiei oferă informații despre produsele Poly Tool, un formular de contact și posibilitatea de abonare la newsletter. Utilizarea site-ului este gratuită.

### 4.2 <a href="https://ortexo.com" target="_blank" rel="noopener noreferrer">Ortexo</a> — Platformă Juridică AI

<a href="https://ortexo.com" target="_blank" rel="noopener noreferrer">Ortexo</a> este o platformă premium de cercetare juridică asistată de inteligență artificială, accesibilă la ortexo.com. <a href="/#features">Detalii complete pe polytooldesign.com →</a>

**Funcționalități disponibile:**
- Acces la peste 500.000 de acte normative românești (legi, ordonanțe, norme, circulare)
- Motor de căutare semantică în jurisprudența ÎCCJ
- Analiză, comparare și urmărire modificări ale documentelor legislative
- Consultant juridic AI disponibil non-stop
- Gestionare calendar, termene și workspace juridic

**Planuri de abonament:**
- **Gratuit** — acces limitat la funcționalitățile de bază
- **Civis** — 49 RON/lună, include TVA
- **Lexis** — 190 RON/lună, include TVA
- **Praetor** — 490 RON/lună, include TVA

**Avertisment important privind limitele serviciului:** Informațiile și răspunsurile furnizate de Ortexo:
- Au caracter **strict informativ și orientativ**
- **Nu constituie consultanță juridică** în sensul Legii nr. 51/1995 privind exercitarea profesiei de avocat
- **Nu înlocuiesc** opinia unui avocat, notar, consilier juridic sau alt specialist juridic autorizat
- Pot conține erori, omisiuni sau interpretări incomplete
- Nu sunt actualizate în timp real — verificați întotdeauna sursa oficială (Monitorul Oficial)

**Poly Tool Design S.R.L. nu răspunde pentru consecințele juridice sau financiare ale deciziilor luate exclusiv pe baza informațiilor furnizate de Ortexo.** Utilizatorii profesioniști (avocați, juristi) rămân integral responsabili pentru calitatea muncii lor juridice.

### 4.3 <a href="/borderou">Borderou</a> — Platformă Web pentru Profesioniști Juridici

<a href="https://borderou.laurasima-cii.ro" target="_blank" rel="noopener noreferrer">Borderou</a> este o platformă web SaaS multi-tenant, destinată profesioniștilor din domeniul juridic (avocați, notari, consilieri juridici), accesibilă la borderou.laurasima-cii.ro.

**Modulul Borderou — Registru de Evidență:**
- Tabele de evidență flexibile cu scheme personalizabile (câmpuri text, numeric, dropdown, PDF)
- Numerotare automată a înregistrărilor în formate configurabile
- Import în masă (până la 500 de înregistrări pe lot) și export în format XLSX și XML
- Arhivare și gestionare tabele

**Modulul Dosare — Urmărire Cauze Judiciare:**
- Căutare și monitorizare dosare prin integrare cu portalul oficial portal.just.ro
- Sincronizare automată nocturnă a cauzelor și notificări în timp real
- Calendar cu termene de judecată și gestionare sarcini (tablou Kanban)
- Analiză AI a documentelor judiciare (Google Vertex AI, europe-west1 — UE) pentru extragerea automată a termenelor

**Colaborare și Organizații:**
- Sistem multi-tenant cu workspace-uri (maximum 3 per utilizator)
- Roluri și permisiuni granulare (proprietar/membru), maximum 100 de membri per organizație

**Planuri de abonament:**
- **Gratuit** — €0/lună: 3 tabele, 500 înregistrări/tabel, 3 membri, fără modulul Dosare
- **Solo** — €8/lună (€80/an): 10 tabele, 5.000 înregistrări/tabel, 10 membri, Dosare inclus, 15 analize AI/lună
- **Pro** — €17/lună (€170/an): 30 tabele, 20.000 înregistrări/tabel, 50 membri, Dosare inclus, 60 analize AI/lună

Utilizatorii noi beneficiază de **1 lună de probă gratuită** la planul Solo la crearea contului. Planurile anuale includ echivalentul a **2 luni gratuite**.

**Condiții de utilizare specifice:** Datele introduse de utilizator (registre, dosare, documente) sunt stocate **exclusiv în cloud** (Cloudflare D1 + Cloudflare R2), cu izolare completă per organizație. Prin utilizarea platformei, acordați consimțământul explicit pentru această stocare. Poly Tool nu accesează conținutul datelor dumneavoastră fără acordul explicit în scris.

**Disponibilitate:** Platforma este dependentă de disponibilitatea infrastructurii Cloudflare. Nu garantăm disponibilitate neîntreruptă 100%, dar ne angajăm să asigurăm cel puțin **99% disponibilitate** anuală măsurată.

### 4.4 Plicuri

**Plicuri** este o aplicație (desktop Electron + backend cloud) pentru practicienii în insolvență, cu sincronizare cloud și procesare AI.

**Funcționalități principale:**
- Gestionarea destinatarilor, firmelor și a bazelor de date (cu validare CUI prin API-ul public ANAF)
- Generarea plicurilor, a borderourilor poștale și a confirmărilor de primire
- Analiza automată (AI) a sentințelor/încheierilor de insolvență și gestionarea dosarelor
- Redactarea corespondenței și a documentelor („Documente AI")
- Stocare cloud (Cloudflare D1 + R2) cu izolare per organizație și facturare pe bază de credite

**Stocare și procesare:** datele introduse sunt stocate în cloud (Cloudflare), iar analiza documentelor este efectuată prin Google Vertex AI (europe-west1, UE). Prin utilizare, acordați consimțământul pentru această stocare și prelucrare, conform <a href="/privacy-policy">Politicii de Confidențialitate</a>.

**Responsabilitatea utilizatorului:** Utilizatorul (practicianul) acționează în calitate de operator de date pentru documentele de insolvență prelucrate și este responsabil pentru legalitatea și conformitatea GDPR a acestora; Poly Tool Design acționează în calitate de persoană împuternicită.

## 5. Conturi de Utilizator

### 5.1 Crearea contului

Conturile Ortexo și Borderou sunt create prin furnizarea unei adrese de email valide și a unei parole. Vă angajați să:
- Furnizați informații corecte, complete și actualizate
- Nu vă impersonați altă persoană sau entitate
- Nu creați conturi multiple sau conturi folosind adrese de email temporare în scopul eludării restricțiilor

### 5.2 Securitatea contului

Sunteți responsabil integral pentru toate activitățile desfășurate din contul dumneavoastră. Vă recomandăm utilizarea unei parole unice și puternice. Notificați-ne imediat la contact@polytooldesign.com în cazul oricărui acces neautorizat sau al suspiciunii de compromitere a contului.

### 5.3 Suspendare și ștergere cont

Ne rezervăm dreptul de a suspenda sau șterge contul dumneavoastră, cu sau fără notificare prealabilă, în caz de:
- Încălcare a acestor Termeni
- Activitate frauduloasă sau ilegală
- Neplată a abonamentului (după notificare prealabilă de 7 zile)
- Inactivitate prelungită (mai mult de 24 luni)

La ștergerea contului, datele asociate sunt păstrate 30 de zile pentru recuperare, după care sunt șterse definitiv (cu excepția documentelor fiscale, care se păstrează 10 ani).

## 6. Drepturi de Proprietate Intelectuală

### 6.1 Drepturile Poly Tool Design

Toate drepturile de proprietate intelectuală asupra site-ului polytooldesign.com, platformei ortexo.com, aplicațiilor Borderou, Corespondenţă și Plicuri — inclusiv design, cod sursă, algoritmi, baze de date, conținut original, interfețe grafice și mărci — aparțin exclusiv **Poly Tool Design S.R.L.** sau licențiatorilor săi și sunt protejate de legislația română și internațională privind proprietatea intelectuală.

Mărcile „**Poly Tool**", „**Poly Tool Design**", „**Ortexo**", „**Borderou**" și logo-urile asociate sunt mărci înregistrate sau în curs de înregistrare ale Poly Tool Design S.R.L.

### 6.2 Licența acordată utilizatorului

Vă acordăm o licență limitată, neexclusivă, netransferabilă și revocabilă pentru a utiliza Serviciile strict conform acestor Termeni. Această licență nu include dreptul de a:
- Copia, modifica, distribui sau vinde orice parte din Servicii sau conținuturi
- Utiliza mărcile noastre fără autorizare scrisă prealabilă
- Dezasambla, decompila sau efectua inginerie inversă a software-ului nostru
- Sublicenția, închiria sau ceda accesul la Servicii unor terți
- Extrage sistematic date din platformele noastre (scraping, crawling)

### 6.3 Conținutul utilizatorului

Vă păstrați drepturile de proprietate intelectuală asupra conținutului pe care îl introduceți în Servicii (dosare, corespondență, documente). Ne acordați o licență limitată pentru a procesa și stoca acest conținut exclusiv în scopul furnizării Serviciilor.

## 7. Politica de Utilizare Acceptabilă

Prin utilizarea Serviciilor, vă angajați că nu veți:

**Activități ilegale:**
- Încălca orice lege sau reglementare aplicabilă, națională sau internațională
- Utiliza Serviciile pentru activități care constituie infracțiuni sau contravenții
- Transmite, stoca sau procesa date obținute prin mijloace ilegale

**Abuzuri tehnice:**
- Tenta accesul neautorizat la sisteme, conturi sau date ale altor utilizatori
- Efectua atacuri de tip denial-of-service (DoS/DDoS) sau alte atacuri cibernetice
- Utiliza boți, crawlere sau scripturi automate fără acordul nostru scris prealabil
- Extrage date în masă din platformele noastre (scraping neautorizat)
- Circumveni măsuri de securitate, de autentificare sau de limitare a ratei de acces

**Conținut inadecvat:**
- Transmite conținut ilegal, defăimător, obscen, amenințător sau care încalcă drepturile terților
- Distribui malware, viruși sau orice cod dăunător prin intermediul Serviciilor

Încălcarea acestei politici poate atrage suspendarea imediată a accesului și, după caz, acțiuni legale.

## 8. Abonamente, Prețuri și Plăți

### 8.1 Prețuri și taxe

- Prețurile Ortexo sunt afișate în **RON** și includ TVA (19%); prețurile Borderou sunt afișate în **EUR** (fără TVA, unde este aplicabil), cu excepția cazurilor în care se specifică altfel
- Facturarea este **lunară sau anuală**, conform opțiunii alese la activarea abonamentului
- Plățile sunt procesate securizat prin **Stripe** — nu stocăm datele cardului dumneavoastră

### 8.2 Reînnoire automată

Abonamentele se reînnoiesc automat la sfârșitul fiecărei perioade de facturare, până la anulare. Veți fi notificat prin email cu cel puțin **7 zile** înainte de orice reînnoire.

### 8.3 Modificări de prețuri

Ne rezervăm dreptul de a modifica prețurile cu notificare prealabilă de minimum **30 de zile** prin email. Dacă nu anulați abonamentul înainte de data efectivă, se consideră că acceptați noul preț.

### 8.4 Întârzieri la plată

În cazul eșecului unei plăți automate, accesul la funcționalitățile premium se suspendă după **7 zile** de la data scadenței, precedat de notificări prin email. Datele sunt păstrate 30 de zile pentru recuperarea contului.

## 9. Politica de Rambursare și Anulare

### 9.1 Dreptul legal de retragere

Conform OUG nr. 34/2014, consumatorii beneficiază de un **drept de retragere de 14 zile calendaristice** de la data primei activări a unui abonament plătit, fără a fi necesară justificarea. Solicitați rambursarea la contact@polytooldesign.com.

### 9.2 Anulare abonament

Puteți anula abonamentul oricând din setările contului. La anulare:
- Accesul rămâne activ până la **sfârșitul perioadei de facturare plătite**
- Nu se emit rambursări pro-rata pentru perioadele parțial utilizate (excepție: perioada de retragere de 14 zile)

### 9.3 Rambursări excepționale

Rambursările în afara perioadei de retragere legale sunt analizate individual, la cerere justificată adresată la contact@polytooldesign.com. Nu garantăm aprobarea lor.

## 10. Declinarea Garanțiilor

Serviciile sunt furnizate **„ca atare"** și **„după cum sunt disponibile"**, fără garanții de nicio natură, exprese sau implicite, inclusiv (fără limitare) garanții de comercializabilitate, adecvare pentru un scop specific sau neîncălcare a drepturilor terților.

Nu garantăm că:
- Serviciile vor fi neîntrerupte, fără erori sau complet exacte
- Informațiile juridice furnizate de Ortexo sunt actualizate, complete sau lipsite de erori
- Serviciile vor îndeplini toate cerințele dumneavoastră specifice

## 11. Limitarea Răspunderii

### 11.1 Excluderi de răspundere

În măsura permisă de dreptul român, Poly Tool Design S.R.L. nu răspunde pentru:
- Pierderi indirecte, incidentale, speciale sau consecvente (pierdere de profit, pierdere de date, pierdere de oportunitate de afaceri)
- Decizii profesionale sau juridice luate pe baza informațiilor furnizate de Ortexo
- Indisponibilitatea temporară a Serviciilor cauzată de terți (Google Cloud, Cloudflare, Firebase)
- Pierderea sau coruperea datelor cauzată de utilizatorul însuși sau de factori externi
- Acțiunile sau omisiunile terților furnizori de servicii

### 11.2 Plafonul răspunderii

Răspunderea noastră totală față de dumneavoastră pentru orice pretenție legată de acești Termeni sau de utilizarea Serviciilor nu va depăși **valoarea sumelor efectiv plătite de dumneavoastră în ultimele 12 luni calendaristice** sau **500 RON**, oricare este mai mare.

Aceste limitări se aplică indiferent de natura acțiunii (contractuală, delictuală sau altfel) și chiar dacă am fost informați de posibilitatea unor astfel de daune.

## 12. Despăgubire

Acceptați să despăgubiți, să apărați și să exonerați Poly Tool Design S.R.L., angajații, directorii, agenții și partenerii săi de orice pretenții, daune, pierderi, costuri și cheltuieli (inclusiv onorarii avocațiale rezonabile) rezultate din:
- Încălcarea de către dumneavoastră a acestor Termeni
- Utilizarea necorespunzătoare sau ilegală a Serviciilor
- Conținutul introdus de dumneavoastră în Servicii
- Încălcarea drepturilor unui terț

## 13. Forță Majoră

Nu răspundem pentru neexecutarea sau întârzierea executării obligațiilor cauzate de evenimente în afara controlului nostru rezonabil, inclusiv: dezastre naturale, pandemii, acțiuni guvernamentale, atacuri cibernetice de amploare, întreruperi ale infrastructurii internet, greve sau alte evenimente similare.

## 14. Modificări ale Termenilor

Ne rezervăm dreptul de a modifica acești Termeni oricând. **Modificările semnificative** vor fi comunicate prin email cu minimum **15 zile** înainte de intrarea în vigoare. Vă recomandăm să verificați periodic această pagină.

Continuarea utilizării Serviciilor după data efectivă a modificărilor constituie acceptarea noii versiuni. Dacă nu acceptați modificările, aveți dreptul să reziliați contractul înainte de data efectivă.

## 15. Reziliere și Efecte

### 15.1 Reziliere de către dumneavoastră

Puteți rezilia oricând utilizarea Serviciilor prin anularea abonamentului și/sau ștergerea contului. Dreptul legal de retragere de 14 zile se aplică conform Secțiunii 9.1.

### 15.2 Reziliere de către Poly Tool

Putem suspenda sau rezilia accesul dumneavoastră cu notificare rezonabilă prealabilă (cel puțin 7 zile, transmisă pe email), cu excepția cazurilor de:
- Încălcare gravă sau repetată a Termenilor
- Activitate frauduloasă sau ilegală — în aceste cazuri, suspendarea poate fi imediată

### 15.3 Efectele rezilierii

La rezilierea contractului:
- Accesul la Servicii încetează la data efectivă
- Datele dumneavoastră sunt păstrate conform perioadelor descrise în <a href="/privacy-policy">Politica de Confidențialitate</a>
- Prevederile referitoare la proprietate intelectuală, limitarea răspunderii, despăgubire și jurisdicție rămân în vigoare

## 16. Legea Aplicabilă și Jurisdicție

Acești Termeni sunt guvernați exclusiv de **dreptul român**. Orice dispută care nu poate fi rezolvată pe cale amiabilă în termen de 30 de zile de la notificare va fi supusă judecății **instanțelor competente din Constanța, România**.

Utilizatorii în calitate de consumatori pot apela și la mecanismele de soluționare alternativă a litigiilor:
- **ANPC — SAL** (Soluționarea Alternativă a Litigiilor): <a href="https://anpc.ro/ce-este-sal/" target="_blank" rel="noopener noreferrer">anpc.ro/ce-este-sal</a>
- **Platforma SOL** (Soluționarea Online a Litigiilor — UE): <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">ec.europa.eu/consumers/odr</a>

## 17. Dispoziții Finale

- **Separabilitate:** Dacă o prevedere este invalidă sau inaplicabilă, celelalte rămân în vigoare
- **Nerenunțare:** Neexercitarea unui drept de către Poly Tool nu constituie renunțare la acel drept
- **Cesiune:** Nu puteți ceda drepturile sau obligațiile din acești Termeni fără acordul nostru scris prealabil. Poly Tool poate ceda drepturile sale în cadrul unei restructurări sau fuziuni, cu notificarea dumneavoastră
- **Acord integral:** Acești Termeni, împreună cu Politica de Confidențialitate și Politica de Cookie-uri, constituie acordul integral dintre dumneavoastră și Poly Tool privind utilizarea Serviciilor

## 18. Contact

Pentru orice întrebări, reclamații sau clarificări privind acești Termeni:

**Poly Tool Design S.R.L.**
Email: <a href="mailto:contact@polytooldesign.com">contact@polytooldesign.com</a>
CUI: RO40293031 | Nr. Reg. Com.: J13/3628/2018
Constanța, România
    `
  },
  en: {
    title: 'Terms & Conditions',
    lastUpdated: 'July 7, 2026',
    content: `
## 1. About Us

These Terms and Conditions ("**Terms**") constitute a legally binding agreement between you and Poly Tool Design S.R.L., governing your use of all our products and services.

**Poly Tool Design S.R.L.**
Registered address: Constanța, Romania | CUI: RO40293031 | Trade Register: J13/3628/2018
Email: <a href="mailto:contact@polytooldesign.com">contact@polytooldesign.com</a>
Website: <a href="https://polytooldesign.com">polytooldesign.com</a>

## 2. Definitions

- **"Services"** — all Poly Tool Design products, platforms, and applications
- **"User" / "you"** — any natural or legal person who accesses or uses the Services
- **"Account"** — the profile created by a user to access services requiring authentication
- **"Content"** — any text, documents, data, or other materials uploaded or created by a user
- **"Subscription"** — paid access to the premium features of Ortexo or Borderou

## 3. Acceptance of Terms

By accessing polytooldesign.com, creating an account on any of our platforms, or installing and using Poly Tool Design applications, you confirm that you have read, understood, and fully accepted these Terms and our <a href="/privacy-policy">Privacy Policy</a>.

If you are using the Services on behalf of a legal entity (company, institution), you warrant that you have the authority to bind that entity and that it accepts these Terms.

If you do not agree with any of these Terms, do not use our Services.

## 4. Description of Services

### 4.1 polytooldesign.com Website

The company's presentation website, providing information about Poly Tool products, a contact form, and the option to subscribe to our newsletter. Use of the website is free.

### 4.2 <a href="https://ortexo.com" target="_blank" rel="noopener noreferrer">Ortexo</a> — AI Legal Platform

<a href="https://ortexo.com" target="_blank" rel="noopener noreferrer">Ortexo</a> is a premium AI-powered legal research platform available at ortexo.com. <a href="/#features">Full details on polytooldesign.com →</a>

**Available features:**
- Access to over 500,000 Romanian legislative acts (laws, ordinances, regulations, circulars)
- Semantic search engine across ÎCCJ (Supreme Court) case law
- Analysis, comparison, and change tracking of legislative documents
- AI legal assistant available around the clock
- Calendar, deadline management, and legal workspace

**Subscription plans:**
- **Free** — limited access to core features
- **Civis** — 49 RON/month, VAT included
- **Lexis** — 190 RON/month, VAT included
- **Praetor** — 490 RON/month, VAT included

**Important disclaimer on service limitations:** Information and responses provided by Ortexo:
- Are for **informational and guidance purposes only**
- Do **not constitute legal advice** within the meaning of Romanian Bar Law No. 51/1995
- Do **not replace** the opinion of a qualified lawyer, notary, legal counsel, or other authorized legal professional
- May contain errors, omissions, or incomplete interpretations
- Are not updated in real time — always verify against the official source (Official Gazette)

**Poly Tool Design S.R.L. is not liable for legal or financial consequences arising from decisions made solely on the basis of information provided by Ortexo.** Legal professionals remain solely responsible for the quality of their legal work.

### 4.3 <a href="/borderou">Borderou</a> — Web Platform for Legal Professionals

<a href="https://borderou.laurasima-cii.ro" target="_blank" rel="noopener noreferrer">Borderou</a> is a multi-tenant web SaaS platform for legal professionals (lawyers, notaries, legal advisors), available at borderou.laurasima-cii.ro.

**Borderou Module — Document Registry:**
- Flexible registry tables with customizable schemas (text, numeric, dropdown, PDF fields)
- Automatic entry numbering in configurable formats
- Bulk import (up to 500 entries per batch) and export to XLSX and XML
- Table archiving and management

**Dosare Module — Court Case Tracking:**
- Case search and monitoring via integration with the official Romanian justice portal (portal.just.ro)
- Automatic nightly case synchronization and real-time notifications
- Calendar with court hearing dates and task management (Kanban board)
- AI-powered judicial document analysis (Google Vertex AI, europe-west1 — EU) for automatic extraction of hearing dates

**Collaboration and Organizations:**
- Multi-tenant system with workspaces (up to 3 per user)
- Granular roles and permissions (owner/member), up to 100 members per organization

**Subscription plans:**
- **Free** — €0/month: 3 tables, 500 entries/table, 3 members, no Dosare module
- **Solo** — €8/month (€80/year): 10 tables, 5,000 entries/table, 10 members, Dosare included, 15 AI analyses/month
- **Pro** — €17/month (€170/year): 30 tables, 20,000 entries/table, 50 members, Dosare included, 60 AI analyses/month

New users receive a **1-month free trial** at the Solo plan upon account creation. Annual plans include the equivalent of **2 months free**.

**Specific terms of use:** Data entered by the user (registries, cases, documents) is stored **exclusively in the cloud** (Cloudflare D1 + Cloudflare R2), with complete per-organization isolation. By using the platform, you grant explicit consent for this storage. Poly Tool does not access the content of your data without explicit written consent.

**Availability:** The platform depends on Cloudflare infrastructure availability. We do not guarantee 100% uninterrupted availability, but we commit to at least **99% annual availability** as measured.

### 4.4 Plicuri

**Plicuri** is an application (Electron desktop + cloud backend) for insolvency practitioners, with cloud synchronization and AI processing.

**Key features:**
- Managing recipients, companies and databases (with CUI validation via the public ANAF API)
- Generating envelopes, postal bordereaux and delivery confirmations
- Automatic (AI) analysis of insolvency court decisions and case management
- Correspondence and document drafting ("Documente AI")
- Cloud storage (Cloudflare D1 + R2) with per-organization isolation and credit-based billing

**Storage and processing:** entered data is stored in the cloud (Cloudflare), and document analysis is performed via Google Vertex AI (europe-west1, EU). By using the application, you consent to this storage and processing, in accordance with our <a href="/privacy-policy">Privacy Policy</a>.

**User responsibility:** The user (practitioner) acts as the data controller for the insolvency documents processed and is responsible for their legality and GDPR compliance; Poly Tool Design acts as the processor.

## 5. User Accounts

### 5.1 Account creation

Ortexo and Borderou accounts are created by providing a valid email address and password. You agree to:
- Provide accurate, complete, and up-to-date information
- Not impersonate any person or entity
- Not create multiple accounts or use temporary email addresses to circumvent restrictions

### 5.2 Account security

You are solely responsible for all activities conducted from your account. We recommend using a unique, strong password. Notify us immediately at contact@polytooldesign.com of any unauthorized access or suspected account compromise.

### 5.3 Account suspension and deletion

We reserve the right to suspend or delete your account, with or without prior notice, in the event of:
- Violation of these Terms
- Fraudulent or illegal activity
- Non-payment of subscription (after 7-day prior notice)
- Extended inactivity (more than 24 months)

Upon account deletion, associated data is retained for 30 days for recovery, then permanently deleted (except fiscal documents, retained for 10 years).

## 6. Intellectual Property Rights

### 6.1 Poly Tool Design's rights

All intellectual property rights in polytooldesign.com, the ortexo.com platform, the Borderou, Corespondenţă, and Plicuri applications — including design, source code, algorithms, databases, original content, graphical interfaces, and trademarks — belong exclusively to **Poly Tool Design S.R.L.** or its licensors, and are protected by Romanian and international intellectual property law.

The trademarks "**Poly Tool**", "**Poly Tool Design**", "**Ortexo**", "**Borderou**" and associated logos are registered or pending trademarks of Poly Tool Design S.R.L.

### 6.2 License granted to user

We grant you a limited, non-exclusive, non-transferable, revocable license to use the Services strictly in accordance with these Terms. This license does not include the right to:
- Copy, modify, distribute, or sell any part of the Services or content
- Use our trademarks without prior written authorization
- Disassemble, decompile, or reverse-engineer our software
- Sub-license, rent, or assign access to the Services to third parties
- Systematically extract data from our platforms (scraping, crawling)

### 6.3 User content

You retain intellectual property rights over the content you enter into the Services (cases, correspondence, documents). You grant us a limited license to process and store this content solely for the purpose of delivering the Services.

## 7. Acceptable Use Policy

By using the Services, you agree that you will not:

**Illegal activities:**
- Violate any applicable law or regulation, national or international
- Use the Services for activities constituting criminal or administrative offenses
- Transmit, store, or process data obtained by illegal means

**Technical abuse:**
- Attempt unauthorized access to systems, accounts, or data of other users
- Carry out denial-of-service (DoS/DDoS) or other cyberattacks
- Use bots, crawlers, or automated scripts without our prior written consent
- Extract data in bulk from our platforms (unauthorized scraping)
- Circumvent security measures, authentication systems, or rate limiting

**Inappropriate content:**
- Transmit illegal, defamatory, obscene, threatening content, or content infringing third-party rights
- Distribute malware, viruses, or any harmful code via the Services

Violation of this policy may result in immediate suspension of access and, where applicable, legal proceedings.

## 8. Subscriptions, Pricing, and Payments

### 8.1 Prices and taxes

- Ortexo prices are displayed in **RON** and include VAT (19%); Borderou prices are displayed in **EUR** (excluding VAT where applicable), unless otherwise stated
- Billing is **monthly or annual**, per the option selected at subscription activation
- Payments are processed securely through **Stripe** — we do not store your card data

### 8.2 Automatic renewal

Subscriptions renew automatically at the end of each billing period, until cancelled. You will be notified by email at least **7 days** before any renewal.

### 8.3 Price changes

We reserve the right to change prices with at least **30 days' advance notice** by email. If you do not cancel your subscription before the effective date, you are deemed to accept the new price.

### 8.4 Payment failures

In the event of a failed automatic payment, access to premium features is suspended after **7 days** from the due date, preceded by email notifications. Data is retained for 30 days for account recovery.

## 9. Cancellation and Refund Policy

### 9.1 Statutory right of withdrawal

Under Romanian Government Emergency Ordinance No. 34/2014, consumers have a **14-calendar-day right of withdrawal** from the date of first activation of a paid subscription, without requiring justification. Request a refund at contact@polytooldesign.com.

### 9.2 Subscription cancellation

You may cancel your subscription at any time from account settings. Upon cancellation:
- Access remains active until the **end of the paid billing period**
- Pro-rata refunds are not issued for partially used periods (exception: the 14-day withdrawal period)

### 9.3 Exceptional refunds

Refunds outside the statutory withdrawal period are reviewed on a case-by-case basis, at justified written request to contact@polytooldesign.com. Approval is not guaranteed.

## 10. Disclaimer of Warranties

The Services are provided **"as is"** and **"as available"**, without warranties of any kind, express or implied, including (without limitation) warranties of merchantability, fitness for a particular purpose, or non-infringement of third-party rights.

We do not warrant that:
- The Services will be uninterrupted, error-free, or completely accurate
- Legal information provided by Ortexo is up-to-date, complete, or free of errors
- The Services will meet all of your specific requirements

## 11. Limitation of Liability

### 11.1 Exclusions

To the maximum extent permitted by Romanian law, Poly Tool Design S.R.L. is not liable for:
- Indirect, incidental, special, or consequential losses (loss of profit, data, or business opportunity)
- Professional or legal decisions made on the basis of information provided by Ortexo
- Temporary unavailability of Services caused by third parties (Google Cloud, Cloudflare, Firebase)
- Loss or corruption of data caused by the user or external factors
- Acts or omissions of third-party service providers

### 11.2 Liability cap

Our total liability to you for any claim relating to these Terms or use of the Services shall not exceed the **amounts actually paid by you in the last 12 calendar months** or **500 RON**, whichever is greater.

These limitations apply regardless of the nature of the claim (contractual, tortious, or otherwise) and even if we have been informed of the possibility of such damages.

## 12. Indemnification

You agree to indemnify, defend, and hold harmless Poly Tool Design S.R.L., its employees, directors, agents, and partners from any claims, damages, losses, costs, and expenses (including reasonable legal fees) arising from:
- Your violation of these Terms
- Your improper or illegal use of the Services
- Content you have entered into the Services
- Your infringement of a third party's rights

## 13. Force Majeure

We are not liable for non-performance or delayed performance of obligations caused by events beyond our reasonable control, including: natural disasters, pandemics, governmental actions, large-scale cyberattacks, internet infrastructure outages, strikes, or similar events.

## 14. Changes to Terms

We reserve the right to modify these Terms at any time. **Significant changes** will be communicated by email at least **15 days** before taking effect. We recommend periodically checking this page.

Continued use of the Services after the effective date of changes constitutes acceptance of the new version. If you do not accept the changes, you are entitled to terminate the agreement before the effective date.

## 15. Termination and Effects

### 15.1 Termination by you

You may terminate use of the Services at any time by cancelling your subscription and/or deleting your account. The statutory 14-day right of withdrawal applies as described in Section 9.1.

### 15.2 Termination by Poly Tool

We may suspend or terminate your access with reasonable prior notice (at least 7 days, sent by email), except in cases of:
- Serious or repeated breach of the Terms
- Fraudulent or illegal activity — in these cases, suspension may be immediate

### 15.3 Effects of termination

Upon termination:
- Access to the Services ceases on the effective date
- Your data is retained in accordance with the periods described in our <a href="/privacy-policy">Privacy Policy</a>
- Provisions relating to intellectual property, limitation of liability, indemnification, and jurisdiction remain in force

## 16. Governing Law and Jurisdiction

These Terms are governed exclusively by **Romanian law**. Any dispute that cannot be resolved amicably within 30 days of notification shall be submitted to the competent courts of **Constanța, Romania**.

Consumer users may also use alternative dispute resolution mechanisms:
- **ANPC — SAL** (Alternative Dispute Resolution): <a href="https://anpc.ro/ce-este-sal/" target="_blank" rel="noopener noreferrer">anpc.ro/ce-este-sal</a>
- **SOL Platform** (Online Dispute Resolution — EU): <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">ec.europa.eu/consumers/odr</a>

## 17. General Provisions

- **Severability:** If any provision is found invalid or unenforceable, the remaining provisions remain in full force
- **No waiver:** Poly Tool's failure to exercise any right does not constitute a waiver of that right
- **Assignment:** You may not assign your rights or obligations under these Terms without our prior written consent. Poly Tool may assign its rights in connection with a restructuring or merger, with notice to you
- **Entire agreement:** These Terms, together with the Privacy Policy and Cookie Policy, constitute the entire agreement between you and Poly Tool regarding use of the Services

## 18. Contact

For any questions, complaints, or clarifications regarding these Terms:

**Poly Tool Design S.R.L.**
Email: <a href="mailto:contact@polytooldesign.com">contact@polytooldesign.com</a>
CUI: RO40293031 | Trade Register: J13/3628/2018
Constanța, Romania
    `
  }
};

const cookies = {
  ro: {
    title: 'Politica de Cookie-uri',
    lastUpdated: '7 Aprilie 2026',
    content: `
## 1. Ce Sunt Cookie-urile?

Cookie-urile sunt fișiere text de mici dimensiuni plasate pe dispozitivul dumneavoastră (computer, tabletă, telefon) atunci când vizitați un site web. Ele sunt stocate de browser-ul dumneavoastră și trimise înapoi site-ului la fiecare vizită ulterioară, permițând site-ului să vă recunoască și să rețină informații despre sesiunea și preferințele dumneavoastră.

Pe lângă cookie-urile clasice, utilizăm și alte tehnologii similare: **localStorage** (stocare persistentă în browser), **sessionStorage** (stocare per sesiune) și **pixeli de urmărire** (imagini mici invizibile pentru analiză).

Această politică acoperă utilizarea cookie-urilor și a tehnologiilor similare **exclusiv pe site-ul polytooldesign.com**. Platforma Ortexo și aplicațiile desktop au propriile mecanisme de autentificare și stocare locală, descrise în <a href="/privacy-policy">Politica de Confidențialitate</a>.

## 2. De Ce Folosim Cookie-uri?

Site-ul polytooldesign.com utilizează cookie-uri pentru:
- Asigurarea funcționării corecte și securizate a site-ului
- Reținerea preferințelor dumneavoastră (ex.: consimțământul cookie-uri)
- Analiza traficului și a comportamentului de navigare pentru optimizarea conținutului
- Măsurarea eficienței comunicărilor de marketing (cu consimțământul dumneavoastră)

## 3. Tipuri de Cookie-uri pe Care le Folosim

### 3.1 Cookie-uri Strict Necesare

Esențiale pentru funcționarea de bază a site-ului. Nu pot fi dezactivate prin preferințele cookie-uri, deoarece fără ele site-ul nu ar funcționa corect. Nu colectează date utilizate în scopuri de marketing.

| Nume cookie | Scop | Durată | Furnizor |
|-------------|------|--------|----------|
| cookieConsent | Stochează consimțământul și preferințele cookie-uri | 12 luni | Poly Tool (propriu) |
| cookieConsentDate | Înregistrează data acordării consimțământului | 12 luni | Poly Tool (propriu) |

### 3.2 Cookie-uri Analitice (Opționale)

Ne ajută să înțelegem cum utilizatorii interacționează cu site-ul — câte persoane vizitează, ce pagini sunt cele mai populare, de unde provin vizitatorii — fără a identifica individual utilizatorii.

**Activare:** Numai cu consimțământul dumneavoastră explicit.

| Nume cookie | Furnizor | Scop | Durată |
|-------------|----------|------|--------|
| _ga | Google Analytics | Identificator unic de utilizator (anonim) | 2 ani |
| _ga_[ID] | Google Analytics | Stochează starea sesiunii | 2 ani |
| _gid | Google Analytics | Distinge utilizatorii unici | 24 ore |
| _gat | Google Analytics | Limitează rata de cereri (throttling) | 1 minut |

Datele colectate prin Google Analytics sunt anonimizate (adresele IP sunt trunchiante) și nu sunt utilizate pentru identificarea personală.

### 3.3 Cookie-uri de Marketing (Opționale)

Utilizate pentru a măsura eficacitatea campaniilor de marketing și, eventual, pentru a vă prezenta publicitate relevantă pe platforme terțe. Activarea este exclusiv opțională.

**Activare:** Numai cu consimțământul dumneavoastră explicit.

| Nume cookie | Furnizor | Scop | Durată |
|-------------|----------|------|--------|
| _fbp | Meta (Facebook) | Urmărire conversii și remarketing | 3 luni |
| _fbc | Meta (Facebook) | Urmărire clicuri din reclame Facebook | 3 luni |

## 4. Cookie-uri Plasate de Terți

Unele funcționalități ale site-ului încarcă conținut sau scripturi de la furnizori terți, care pot plasa propriile cookie-uri pe dispozitivul dumneavoastră. Acești furnizori sunt:

- **Google Analytics (Google LLC)** — analiză trafic. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Politica de confidențialitate Google →</a>
- **Mailchimp (Intuit Inc.)** — servicii newsletter, formular de abonare. <a href="https://mailchimp.com/legal/privacy/" target="_blank" rel="noopener noreferrer">Politica Mailchimp →</a>
- **Web3Forms** — procesare formulare contact. <a href="https://web3forms.com/privacy" target="_blank" rel="noopener noreferrer">Politica Web3Forms →</a>
- **Meta Pixel (Meta Platforms)** — numai dacă ați acceptat cookie-urile de marketing. <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer">Politica Meta →</a>

Poly Tool nu controlează cookie-urile plasate de acești furnizori. Vă recomandăm să consultați politicile lor de confidențialitate pentru detalii complete.

## 5. Cum Gestionați Cookie-urile

### 5.1 Prin bannerul nostru de cookie-uri

La prima vizită pe polytooldesign.com, veți vedea bannerul nostru de consimțământ. Aveți trei opțiuni:
- **Accept toate** — activează toate cookie-urile (necesare + analitice + marketing)
- **Doar necesare** — activează exclusiv cookie-urile esențiale
- **Personalizează** — alegeți individual fiecare categorie

Preferințele dumneavoastră sunt stocate timp de **12 luni**. Puteți modifica oricând preferințele accesând linkul „Preferințe cookie-uri" din subsolul site-ului.

### 5.2 Prin setările browser-ului

Puteți gestiona, bloca sau șterge cookie-urile direct din setările browser-ului:

- **Google Chrome:** Setări → Confidențialitate și securitate → Cookie-uri și alte date ale site-urilor
- **Mozilla Firefox:** Opțiuni → Confidențialitate și securitate → Cookie-uri și date ale site-urilor
- **Apple Safari:** Preferințe → Confidențialitate → Gestionare date site-uri
- **Microsoft Edge:** Setări → Cookie-uri și permisiuni site

### 5.3 Opțiuni de dezactivare a urmăririi (Opt-out)

- **Google Analytics:** Instalați extensia <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on →</a>
- **Meta/Facebook:** Ajustați setările de reclame la <a href="https://www.facebook.com/ads/preferences" target="_blank" rel="noopener noreferrer">facebook.com/ads/preferences →</a>
- **Reclame online (general):** <a href="https://www.youronlinechoices.eu/" target="_blank" rel="noopener noreferrer">youronlinechoices.eu →</a>

## 6. Impactul Dezactivării Cookie-urilor

Dezactivarea cookie-urilor strict necesare poate afecta funcționalitatea de bază a site-ului (ex.: preferințele nu vor fi reținute). Dezactivarea celor analitice și de marketing nu afectează funcționarea site-ului, dar poate duce la afișarea de reclame mai puțin relevante.

## 7. Tehnologii Similare

Pe lângă cookie-uri, utilizăm:

- **localStorage** — pentru stocarea persistentă a preferințelor dumneavoastră în browser (ex.: preferințele cookie-uri, tema vizuală). Datele rămân pe dispozitivul dumneavoastră și nu expiră automat
- **sessionStorage** — pentru stocarea temporară a datelor de sesiune; se șterge automat la închiderea tabului/browser-ului
- **Pixeli de urmărire** — imagini mici (1×1 pixel) incluse în emailurile noastre de newsletter, care ne permit să detectăm dacă emailul a fost deschis și pe ce link-uri s-a dat clic. Puteți dezactiva pixelii de urmărire din setările clientului de email

## 8. Actualizări ale Politicii

Putem actualiza această politică pe măsură ce adăugăm noi funcționalități sau modificăm furnizorii de servicii. Data ultimei actualizări este indicată la începutul documentului. Modificările semnificative vor fi anunțate prin bannerul de cookie-uri sau prin email.

## 9. Drepturile Dumneavoastră

Conform GDPR, aveți dreptul să:
- **Retrageți consimțământul** oricând, fără a afecta prelucrările anterioare
- **Accesați** datele colectate prin cookie-uri analitice și de marketing
- **Solicitați ștergerea** datelor colectate prin aceste cookie-uri
- **Vă opuneți** prelucrării datelor în scop de marketing

Exercitați aceste drepturi prin bannerul de preferințe cookie-uri sau contactând-ne la contact@polytooldesign.com.

## 10. Contact

Pentru întrebări despre utilizarea cookie-urilor pe polytooldesign.com:

**Poly Tool Design S.R.L.**
Email: <a href="mailto:contact@polytooldesign.com">contact@polytooldesign.com</a>
CUI: RO40293031 | Constanța, România

**Resurse suplimentare despre cookie-uri:**
- <a href="https://www.aboutcookies.org" target="_blank" rel="noopener noreferrer">aboutcookies.org</a>
- <a href="https://www.youronlinechoices.eu" target="_blank" rel="noopener noreferrer">youronlinechoices.eu</a>
- <a href="https://www.dataprotection.ro" target="_blank" rel="noopener noreferrer">dataprotection.ro (ANSPDCP)</a>
    `
  },
  en: {
    title: 'Cookie Policy',
    lastUpdated: 'April 7, 2026',
    content: `
## 1. What Are Cookies?

Cookies are small text files placed on your device (computer, tablet, phone) when you visit a website. They are stored by your browser and sent back to the website on each subsequent visit, allowing the site to recognize you and remember information about your session and preferences.

In addition to traditional cookies, we also use similar technologies: **localStorage** (persistent browser storage), **sessionStorage** (per-session storage), and **tracking pixels** (small invisible images for analytics).

This policy covers the use of cookies and similar technologies **exclusively on the polytooldesign.com website**. The Ortexo platform and desktop applications have their own authentication and local storage mechanisms, described in our <a href="/privacy-policy">Privacy Policy</a>.

## 2. Why We Use Cookies

The polytooldesign.com website uses cookies to:
- Ensure the correct and secure functioning of the website
- Remember your preferences (e.g., cookie consent)
- Analyze traffic and browsing behavior to optimize content
- Measure the effectiveness of marketing communications (with your consent)

## 3. Types of Cookies We Use

### 3.1 Strictly Necessary Cookies

Essential for the basic functioning of the website. They cannot be disabled through cookie preferences, as the website would not function properly without them. They do not collect data used for marketing purposes.

| Cookie name | Purpose | Duration | Provider |
|-------------|---------|----------|---------|
| cookieConsent | Stores cookie consent and preferences | 12 months | Poly Tool (own) |
| cookieConsentDate | Records the date consent was given | 12 months | Poly Tool (own) |

### 3.2 Analytics Cookies (Optional)

Help us understand how users interact with the website — how many people visit, which pages are most popular, where visitors come from — without individually identifying users.

**Activation:** Only with your explicit consent.

| Cookie name | Provider | Purpose | Duration |
|-------------|---------|---------|---------|
| _ga | Google Analytics | Anonymous unique user identifier | 2 years |
| _ga_[ID] | Google Analytics | Session state storage | 2 years |
| _gid | Google Analytics | Distinguishes unique users | 24 hours |
| _gat | Google Analytics | Request rate limiting (throttling) | 1 minute |

Data collected through Google Analytics is anonymized (IP addresses are truncated) and is not used for personal identification.

### 3.3 Marketing Cookies (Optional)

Used to measure the effectiveness of marketing campaigns and, optionally, to present you with relevant advertising on third-party platforms. Activation is entirely optional.

**Activation:** Only with your explicit consent.

| Cookie name | Provider | Purpose | Duration |
|-------------|---------|---------|---------|
| _fbp | Meta (Facebook) | Conversion tracking and remarketing | 3 months |
| _fbc | Meta (Facebook) | Click tracking from Facebook ads | 3 months |

## 4. Third-Party Cookies

Some website features load content or scripts from third-party providers, which may place their own cookies on your device. These providers are:

- **Google Analytics (Google LLC)** — traffic analysis. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy →</a>
- **Mailchimp (Intuit Inc.)** — newsletter services, subscription form. <a href="https://mailchimp.com/legal/privacy/" target="_blank" rel="noopener noreferrer">Mailchimp Privacy Policy →</a>
- **Web3Forms** — contact form processing. <a href="https://web3forms.com/privacy" target="_blank" rel="noopener noreferrer">Web3Forms Privacy Policy →</a>
- **Meta Pixel (Meta Platforms)** — only if you have accepted marketing cookies. <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer">Meta Privacy Policy →</a>

Poly Tool does not control cookies placed by these providers. We recommend consulting their privacy policies for full details.

## 5. Managing Your Cookie Preferences

### 5.1 Through our cookie banner

On your first visit to polytooldesign.com, you will see our consent banner. You have three options:
- **Accept all** — activates all cookies (necessary + analytics + marketing)
- **Necessary only** — activates only essential cookies
- **Customize** — choose each category individually

Your preferences are stored for **12 months**. You can change your preferences at any time by clicking the "Cookie Preferences" link in the website footer.

### 5.2 Through browser settings

You can manage, block, or delete cookies directly from your browser settings:

- **Google Chrome:** Settings → Privacy and security → Cookies and other site data
- **Mozilla Firefox:** Options → Privacy & Security → Cookies and Site Data
- **Apple Safari:** Preferences → Privacy → Manage Website Data
- **Microsoft Edge:** Settings → Cookies and site permissions

### 5.3 Tracking opt-out options

- **Google Analytics:** Install the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on →</a>
- **Meta/Facebook:** Adjust ad settings at <a href="https://www.facebook.com/ads/preferences" target="_blank" rel="noopener noreferrer">facebook.com/ads/preferences →</a>
- **Online ads (general):** <a href="https://www.youronlinechoices.eu/" target="_blank" rel="noopener noreferrer">youronlinechoices.eu →</a>

## 6. Impact of Disabling Cookies

Disabling strictly necessary cookies may affect the basic functionality of the website (e.g., preferences will not be remembered). Disabling analytics and marketing cookies does not affect website functionality, but may result in less relevant advertising.

## 7. Similar Technologies

In addition to cookies, we use:

- **localStorage** — for persistent storage of your browser preferences (e.g., cookie preferences, visual theme). Data remains on your device and does not expire automatically
- **sessionStorage** — for temporary storage of session data; automatically deleted when the tab/browser is closed
- **Tracking pixels** — small images (1×1 pixel) included in our newsletter emails that allow us to detect whether an email was opened and which links were clicked. You can disable tracking pixels in your email client settings

## 8. Updates to This Policy

We may update this policy as we add new features or change service providers. The date of the last update is shown at the top of this document. Significant changes will be announced via the cookie banner or by email.

## 9. Your Rights

Under GDPR, you have the right to:
- **Withdraw consent** at any time, without affecting prior processing
- **Access** data collected through analytics and marketing cookies
- **Request deletion** of data collected through these cookies
- **Object** to the processing of data for marketing purposes

Exercise these rights through the cookie preference banner or by contacting us at contact@polytooldesign.com.

## 10. Contact

For questions about the use of cookies on polytooldesign.com:

**Poly Tool Design S.R.L.**
Email: <a href="mailto:contact@polytooldesign.com">contact@polytooldesign.com</a>
CUI: RO40293031 | Constanța, Romania

**Further resources about cookies:**
- <a href="https://www.aboutcookies.org" target="_blank" rel="noopener noreferrer">aboutcookies.org</a>
- <a href="https://www.youronlinechoices.eu" target="_blank" rel="noopener noreferrer">youronlinechoices.eu</a>
- <a href="https://www.dataprotection.ro" target="_blank" rel="noopener noreferrer">dataprotection.ro (ANSPDCP)</a>
    `
  }
};

const dpa = {
  ro: {
    title: 'Acord de Prelucrare a Datelor (DPA)',
    lastUpdated: '7 Iulie 2026',
    content: `
## Preambul și Context

Prezentul Acord de Prelucrare a Datelor cu Caracter Personal („**Acordul DPA**" sau „**DPA**"), încheiat conform art. 28 din Regulamentul (UE) 2016/679 (GDPR), se aplică între:

**PERSOANA ÎMPUTERNICITĂ (Procesatorul):** Poly Tool Design S.R.L., cu sediul în Constanța, România, CUI RO40293031, Nr. Reg. Com. J13/3628/2018 („**Poly Tool**", „**Procesatorul**").

**OPERATORUL (Clientul):** practicianul în insolvență sau entitatea care a activat un abonament la aplicația **Plicuri** și care prelucrează, prin intermediul acesteia, date cu caracter personal ale terților.

**Context:** Prin utilizarea aplicației Plicuri, Clientul încarcă, stochează și procesează documente de insolvență (sentințe, încheieri, tabele de creditori, corespondență) care conțin date cu caracter personal ale terților. În această situație, Clientul acționează în calitate de **operator** (art. 4 pct. 7 GDPR), iar Poly Tool în calitate de **persoană împuternicită** (art. 4 pct. 8 GDPR). Prezentul DPA constituie contractul cerut de art. 28 GDPR și face parte integrantă din <a href="/terms">Termenii și Condițiile</a> aplicației Plicuri.

**Prin activarea unui abonament Plicuri și prin utilizarea funcționalităților care implică prelucrarea datelor cu caracter personal ale terților, Clientul acceptă integral prezentul DPA.**

## 1. Definiții

Termenii au semnificațiile din GDPR, cu următoarele precizări:
- **„Date Prelucrate"** — datele cu caracter personal ale terților (debitori, creditori, reprezentanți, persoane fizice parte în dosare etc.) pe care Clientul le încarcă sau procesează prin Plicuri.
- **„Serviciul"** — aplicația Plicuri, astfel cum este definită în Termeni și Condiții.
- **„Sub-Procesator"** — orice terț angajat de Poly Tool pentru activități de prelucrare în numele Clientului (Secțiunea 6).
- **„Instrucțiuni"** — instrucțiunile documentate ale Operatorului, reflectate în Termeni și în prezentul DPA.
- **„Incident de Securitate"** — o încălcare a securității care duce la distrugerea, pierderea, alterarea, divulgarea neautorizată sau accesul neautorizat la Datele Prelucrate.

## 2. Obiectul, Natura și Durata Prelucrării

### 2.1 Obiectul
Poly Tool prelucrează Datele Prelucrate exclusiv pentru furnizarea Serviciului Plicuri, conform instrucțiunilor documentate ale Clientului și prezentului DPA.

### 2.2 Natura prelucrării
Activitățile de prelucrare includ:
- Stocarea documentelor și datelor pe infrastructura cloud (Cloudflare D1 + R2), cu izolare per organizație;
- Extragerea structurată a datelor din documente prin sisteme AI (**Google Vertex AI, regiunea europe-west1 — UE**), la solicitarea Clientului;
- Autentificarea utilizatorilor (Firebase);
- Validarea codurilor fiscale prin API-ul public ANAF și căutarea de informații publice;
- Accesul tehnic necesar depanării și suportului; backup și restaurare.

### 2.3 Scopul
Poly Tool prelucrează Datele Prelucrate exclusiv pentru furnizarea Serviciului, nu în scopuri proprii, cu excepția obligațiilor legale sau a asigurării securității platformei.

### 2.4 Durata
Prezentul DPA rămâne în vigoare pe durata activă a abonamentului Clientului. La încetare, se aplică Secțiunea 10 (Returnarea și Ștergerea Datelor).

## 3. Tipul Datelor și Categoriile de Persoane Vizate

### 3.1 Tipul datelor
Categoriile sunt determinate exclusiv de Client prin conținutul documentelor și pot include, fără a fi limitative: date de identificare (nume, CNP, CUI), date de contact (adresă, telefon, e-mail), date financiare și patrimoniale, date privind proceduri judiciare (calitate procesuală, dosare), și orice alte date prezente în documentele de insolvență încărcate.

### 3.2 Date speciale (sensibile)
Plicuri **nu este proiectată pentru prelucrarea la scară a categoriilor speciale de date (art. 9 GDPR)**. Clientul este singurul responsabil pentru evaluarea adecvării Serviciului și, dacă este cazul, pentru efectuarea unei Evaluări a Impactului asupra Protecției Datelor (DPIA) conform art. 35 GDPR.

### 3.3 Categorii de persoane vizate
Determinate exclusiv de Client; pot include: debitori, creditori, reprezentanți legali, administratori/lichidatori judiciari, magistrați, martori, experți, destinatari ai corespondenței și orice persoane fizice ale căror date apar în documentele procesate.

## 4. Obligațiile Procesatorului (Poly Tool)

### 4.1 Prelucrare pe bază de instrucțiuni
Poly Tool prelucrează Datele Prelucrate exclusiv pe baza instrucțiunilor documentate ale Clientului, inclusiv cu privire la transferuri, cu excepția obligațiilor legale — caz în care informează Clientul în prealabil, dacă legea permite.

### 4.2 Confidențialitate
Persoanele autorizate de Poly Tool să prelucreze Datele Prelucrate s-au angajat să respecte confidențialitatea sau au o obligație legală adecvată în acest sens.

### 4.3 Securitate
Poly Tool implementează măsuri tehnice și organizatorice adecvate riscului (art. 32 GDPR), descrise în <a href="/privacy-policy">Politica de Confidențialitate</a> (Secțiunea 10): criptare în tranzit și la repaus, izolare per organizație, control al accesului.

### 4.4 Recurgerea la Sub-Procesatori
Poly Tool nu angajează un Sub-Procesator fără autorizarea Clientului. Prin acceptarea prezentului DPA, Clientul acordă autorizarea generală pentru Sub-Procesatorii din Secțiunea 6 și va fi informat cu privire la orice modificare intenționată, având posibilitatea de a se opune.

### 4.5 Asistarea Operatorului
Poly Tool asistă Clientul, prin măsuri adecvate, în a răspunde cererilor persoanelor vizate (acces, rectificare, ștergere, restricționare, portabilitate, opoziție) și în respectarea obligațiilor prevăzute la art. 32-36 GDPR. Dacă o persoană vizată se adresează direct Poly Tool, solicitarea este redirecționată Clientului fără întârziere.

### 4.6 Ștergerea sau returnarea datelor
La alegerea Clientului, Poly Tool șterge sau returnează Datele Prelucrate după încetarea Serviciului, conform Secțiunii 10.

### 4.7 Informații și audituri
Poly Tool pune la dispoziția Clientului informațiile necesare demonstrării conformității cu art. 28 GDPR și permite audituri, în condițiile Secțiunii 9.

## 5. Obligațiile Operatorului (Clientul)

Clientul, în calitate de operator, se angajează să:
- Se asigure de existența unui temei juridic valid (art. 6 și, dacă este cazul, art. 9 GDPR);
- Informeze persoanele vizate cu privire la prelucrare (art. 13-14 GDPR);
- Respecte toate obligațiile de operator, inclusiv răspunsul la cererile persoanelor vizate;
- Evalueze adecvarea Serviciului înainte de a încărca date și să efectueze o DPIA atunci când este necesar;
- Nu transmită instrucțiuni care ar conduce la încălcarea GDPR;
- Notifice fără întârziere Poly Tool cu privire la orice modificare a instrucțiunilor de prelucrare.

## 6. Sub-Procesatori Autorizați

Prin acceptare, Clientul acordă autorizarea generală pentru Sub-Procesatorii de mai jos. Poly Tool impune fiecăruia obligații echivalente celor din prezentul DPA, prin contracte conforme cu art. 28 alin. (4) GDPR.

| Sub-Procesator | Activitate | Locație prelucrare | Garanție transfer |
|----------------|-----------|--------------------|-------------------|
| Google Cloud — Vertex AI (europe-west1) | Extragere AI din documente (sentințe, PDF-uri, probe) | UE (Belgia) | — (prelucrare în UE) |
| Cloudflare Inc. | API backend + bază de date (D1) + stocare (R2) | UE/SUA | Clauze Contractuale Standard (SCC) |
| Firebase / Google LLC | Autentificare utilizatori | UE/SUA | SCC |
| Stripe Inc. | Procesare plăți (credite) | SUA | SCC + Data Privacy Framework |
| ANAF | Validare CUI (API public) | România (UE) | — |
| Brave Software / DuckDuckGo | Căutare informații publice | UE/SUA | SCC |

## 7. Transferuri Internaționale de Date

Prelucrarea AI a documentelor (Google Vertex AI) se efectuează în **UE, regiunea europe-west1 (Belgia)**, fără transfer în afara UE. Pentru Sub-Procesatorii care prelucrează în afara SEE, transferurile se întemeiază pe **Clauze Contractuale Standard (Decizia 2021/914/UE)** și, unde este aplicabil, pe **EU-U.S. Data Privacy Framework**.

## 8. Incidente de Securitate

Poly Tool notifică Clientul fără întârziere nejustificată după ce ia cunoștință de un Incident de Securitate care afectează Datele Prelucrate, furnizând informațiile necesare pentru ca Clientul să își respecte propriile obligații de notificare (art. 33-34 GDPR).

## 9. Audituri

Poly Tool pune la dispoziția Clientului informațiile necesare demonstrării conformității și permite audituri (inclusiv inspecții) efectuate de Client sau de un auditor mandatat, cu notificare prealabilă rezonabilă, în timpul programului de lucru și fără a compromite securitatea sau confidențialitatea altor clienți.

## 10. Returnarea și Ștergerea Datelor

La încetarea Serviciului, Poly Tool șterge sau returnează Datele Prelucrate, la alegerea Clientului, și șterge copiile existente, cu excepția cazurilor în care legea UE sau română impune păstrarea. Termenele de ștergere sunt cele prevăzute în <a href="/privacy-policy">Politica de Confidențialitate</a> (Secțiunea 8).

## 11. Răspundere și Dispoziții Finale

Răspunderea părților este guvernată de <a href="/terms">Termenii și Condițiile</a> Plicuri și de GDPR. Prezentul DPA este guvernat de legea română. În caz de conflict între prezentul DPA și Termeni cu privire la prelucrarea datelor cu caracter personal, prevalează DPA.

## 12. Contact

**Poly Tool Design S.R.L.** — Constanța, România | CUI: RO40293031
Email: <a href="mailto:contact@polytooldesign.com">contact@polytooldesign.com</a>

Autoritatea de supraveghere: **ANSPDCP** — <a href="https://www.dataprotection.ro" target="_blank" rel="noopener noreferrer">www.dataprotection.ro</a>
    `
  },
  en: {
    title: 'Data Processing Agreement (DPA)',
    lastUpdated: 'July 7, 2026',
    content: `
## Preamble and Context

This Data Processing Agreement ("**DPA**"), entered into under Article 28 of Regulation (EU) 2016/679 (GDPR), applies between:

**THE PROCESSOR:** Poly Tool Design S.R.L., headquartered in Constanța, Romania, CUI RO40293031, Trade Register No. J13/3628/2018 ("**Poly Tool**", "**the Processor**").

**THE CONTROLLER (Client):** the insolvency practitioner or entity that has activated a subscription to the **Plicuri** application and that processes, through it, personal data of third parties.

**Context:** By using Plicuri, the Client uploads, stores and processes insolvency documents (court decisions, creditor tables, correspondence) that contain personal data of third parties. In this situation, the Client acts as the **controller** (Art. 4(7) GDPR) and Poly Tool as the **processor** (Art. 4(8) GDPR). This DPA constitutes the contract required by Art. 28 GDPR and forms an integral part of the <a href="/terms">Terms & Conditions</a> of the Plicuri application.

**By activating a Plicuri subscription and using features that involve processing third-party personal data, the Client accepts this DPA in full.**

## 1. Definitions

Terms have the meanings defined in the GDPR, with the following clarifications:
- **"Processed Data"** — the third-party personal data (debtors, creditors, representatives, individuals party to cases, etc.) that the Client uploads or processes through Plicuri.
- **"the Service"** — the Plicuri application as defined in the Terms & Conditions.
- **"Sub-Processor"** — any third party engaged by Poly Tool to carry out processing activities on the Client's behalf (Section 6).
- **"Instructions"** — the Controller's documented instructions, as reflected in the Terms and this DPA.
- **"Security Incident"** — a breach of security leading to the accidental or unlawful destruction, loss, alteration, unauthorized disclosure of, or access to, the Processed Data.

## 2. Subject Matter, Nature and Duration of Processing

### 2.1 Subject matter
Poly Tool processes the Processed Data solely to provide the Plicuri Service, in accordance with the Client's documented instructions and this DPA.

### 2.2 Nature of the processing
Processing activities include:
- Storing documents and data on cloud infrastructure (Cloudflare D1 + R2), with per-organization isolation;
- Structured extraction of data from documents via AI systems (**Google Vertex AI, europe-west1 region — EU**), at the Client's request;
- User authentication (Firebase);
- Tax-ID validation via the public ANAF API and public-information search;
- Technical access needed for troubleshooting and support; backup and restore.

### 2.3 Purpose
Poly Tool processes the Processed Data solely to provide the Service, not for its own purposes, except where required by law or necessary for platform security.

### 2.4 Duration
This DPA remains in force for the active duration of the Client's subscription. Upon termination, Section 10 (Return and Deletion of Data) applies.

## 3. Types of Data and Categories of Data Subjects

### 3.1 Types of data
The categories are determined solely by the Client through the content of the documents and may include, without limitation: identification data (name, national ID, tax ID), contact data (address, phone, email), financial and asset data, data on judicial proceedings (procedural capacity, cases), and any other data present in the uploaded insolvency documents.

### 3.2 Special (sensitive) data
Plicuri **is not designed for large-scale processing of the special categories of data (Art. 9 GDPR)**. The Client is solely responsible for assessing the suitability of the Service and, where applicable, for carrying out a Data Protection Impact Assessment (DPIA) under Art. 35 GDPR.

### 3.3 Categories of data subjects
Determined solely by the Client; may include: debtors, creditors, legal representatives, judicial administrators/liquidators, magistrates, witnesses, experts, correspondence recipients, and any individuals whose data appears in the processed documents.

## 4. Processor Obligations (Poly Tool)

### 4.1 Processing on documented instructions
Poly Tool processes the Processed Data solely on the Client's documented instructions, including as to transfers, unless required by law — in which case it informs the Client beforehand, where the law permits.

### 4.2 Confidentiality
Persons authorized by Poly Tool to process the Processed Data have committed to confidentiality or are under an appropriate statutory obligation of confidentiality.

### 4.3 Security
Poly Tool implements technical and organizational measures appropriate to the risk (Art. 32 GDPR), described in the <a href="/privacy-policy">Privacy Policy</a> (Section 10): encryption in transit and at rest, per-organization isolation, access control.

### 4.4 Engaging Sub-Processors
Poly Tool does not engage a Sub-Processor without the Client's authorization. By accepting this DPA, the Client grants general authorization for the Sub-Processors in Section 6 and will be informed of any intended change, with the opportunity to object.

### 4.5 Assisting the Controller
Poly Tool assists the Client, by appropriate measures, in responding to data-subject requests (access, rectification, erasure, restriction, portability, objection) and in complying with Art. 32-36 GDPR. If a data subject contacts Poly Tool directly, the request is forwarded to the Client without delay.

### 4.6 Deletion or return of data
At the Client's choice, Poly Tool deletes or returns the Processed Data after the Service ends, per Section 10.

### 4.7 Information and audits
Poly Tool makes available the information necessary to demonstrate compliance with Art. 28 GDPR and allows for audits, under the conditions of Section 9.

## 5. Controller Obligations (Client)

The Client, as controller, undertakes to:
- Ensure a valid legal basis exists (Art. 6 and, where applicable, Art. 9 GDPR);
- Inform data subjects about the processing (Art. 13-14 GDPR);
- Comply with all controller obligations, including responding to data-subject requests;
- Assess the suitability of the Service before uploading data and carry out a DPIA when necessary;
- Not issue instructions that would breach the GDPR;
- Notify Poly Tool without delay of any change to the processing instructions.

## 6. Authorized Sub-Processors

By accepting this DPA, the Client grants general authorization for the Sub-Processors below. Poly Tool imposes on each equivalent obligations to those in this DPA, through contracts compliant with Art. 28(4) GDPR.

| Sub-Processor | Activity | Processing location | Transfer safeguard |
|---------------|----------|---------------------|--------------------|
| Google Cloud — Vertex AI (europe-west1) | AI extraction from documents (court decisions, PDFs, evidence) | EU (Belgium) | — (processed in the EU) |
| Cloudflare Inc. | Backend API + database (D1) + storage (R2) | EU/USA | Standard Contractual Clauses (SCC) |
| Firebase / Google LLC | User authentication | EU/USA | SCC |
| Stripe Inc. | Payment processing (credits) | USA | SCC + Data Privacy Framework |
| ANAF | Public CUI validation API | Romania (EU) | — |
| Brave Software / DuckDuckGo | Public information search | EU/USA | SCC |

## 7. International Data Transfers

AI processing of documents (Google Vertex AI) is performed in the **EU, europe-west1 (Belgium)** region, with no transfer outside the EU. For Sub-Processors that process outside the EEA, transfers rely on **Standard Contractual Clauses (Decision 2021/914/EU)** and, where applicable, the **EU-U.S. Data Privacy Framework**.

## 8. Security Incidents

Poly Tool notifies the Client without undue delay after becoming aware of a Security Incident affecting the Processed Data, providing the information the Client needs to meet its own notification obligations (Art. 33-34 GDPR).

## 9. Audits

Poly Tool makes available the information necessary to demonstrate compliance and allows for audits (including inspections) conducted by the Client or a mandated auditor, on reasonable prior notice, during business hours and without compromising the security or confidentiality of other clients.

## 10. Return and Deletion of Data

Upon termination of the Service, Poly Tool deletes or returns the Processed Data, at the Client's choice, and deletes existing copies, except where EU or Romanian law requires retention. Deletion periods are those set out in the <a href="/privacy-policy">Privacy Policy</a> (Section 8).

## 11. Liability and Final Provisions

The parties' liability is governed by the Plicuri <a href="/terms">Terms & Conditions</a> and by the GDPR. This DPA is governed by Romanian law. In the event of a conflict between this DPA and the Terms regarding the processing of personal data, this DPA prevails.

## 12. Contact

**Poly Tool Design S.R.L.** — Constanța, Romania | CUI: RO40293031
Email: <a href="mailto:contact@polytooldesign.com">contact@polytooldesign.com</a>

Supervisory authority: **ANSPDCP** — <a href="https://www.dataprotection.ro" target="_blank" rel="noopener noreferrer">www.dataprotection.ro</a>
    `
  }
};

export const legalContent = { privacy, terms, cookies, dpa };
